<template>
  <section class="rounded-[28px] border border-[#eee4da] bg-white shadow-sm" :class="isPartnerEditorRoute ? 'overflow-visible' : 'overflow-hidden'">
    <div class="flex flex-wrap items-center justify-between gap-4 border-b border-[#eee4da] px-10 py-6">
      <div class="flex items-center gap-4">
        <button type="button" class="text-xl text-muted transition hover:text-brand" @click="goBack">
          <i class="ri-arrow-left-line"></i>
        </button>
        <div>
          <div class="flex items-center gap-3">
            <h2 class="text-[2rem] font-semibold text-ink">{{ pageTitle }}</h2>
            <span class="rounded-full bg-[#F6EEE5] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand">{{ moduleBadge }}</span>
            <span class="rounded-full bg-[#f6f1ea] px-3 py-1 text-xs font-semibold text-[#7b624d]">{{ isEditMode ? 'Draft' : 'New' }}</span>
          </div>
          <p class="mt-1 text-sm text-muted">{{ businessName }}</p>
        </div>
      </div>

    </div>

    <div v-if="isPartnerEditorRoute" class="px-8 py-8 lg:px-9">
      <div class="mx-auto max-w-5xl space-y-6">
        <div class="flex flex-wrap items-start justify-between gap-4 rounded-[24px] border border-[#eadfd4] bg-[#fdf8f3] px-6 py-5">
          <div>
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Vendor partner</p>
            <h3 class="mt-2 font-serif text-[2rem] font-semibold text-ink">{{ partnerEditorHeading }}</h3>
            <p class="mt-2 max-w-3xl text-sm text-muted">Add preferred partners connected to this venue so your team can reference trusted vendors quickly.</p>
          </div>
          <button
            type="button"
            class="rounded-xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand"
            @click="closePartnerForm"
          >
            Back to partners
          </button>
        </div>

        <div v-if="!currentVenueId" class="rounded-2xl border border-dashed border-[#eadfd4] bg-white p-5 text-sm text-muted">
          Save the venue identity first before adding vendor partners.
        </div>

        <section v-else class="overflow-visible rounded-[24px] border border-dashed border-brand bg-white p-5">
          <h3 class="font-serif text-[1.75rem] font-semibold text-brand">{{ partnerEditorHeading }}</h3>
          <div class="mt-5 grid gap-4 md:grid-cols-3">
            <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
              Name
              <input v-model.trim="partnerForm.name" type="text" placeholder="e.g. Bloom Decor Studio" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              <p v-if="partnerFieldErrors.name" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ partnerFieldErrors.name }}</p>
            </label>
            <label class="overflow-visible text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
              Category
              <div class="relative mt-2">
                <button
                  type="button"
                  class="flex w-full items-center justify-between rounded-2xl border border-[#e8ddd1] bg-white px-4 py-3 text-left text-sm normal-case font-normal focus:border-brand focus:outline-none"
                  @click="togglePartnerCategoryDropdown"
                >
                  <span :class="partnerForm.category ? 'text-ink' : 'text-muted'">{{ getPartnerCategoryLabel(partnerForm.category) || 'Select...' }}</span>
                  <i class="text-base text-muted" :class="partnerCategoryDropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                </button>

                <div v-if="partnerCategoryDropdownOpen" class="absolute z-30 mt-2 w-full overflow-hidden rounded-2xl border border-[#eadfd4] bg-white shadow-xl">
                  <div class="border-b border-[#f0e5da] p-3">
                    <div class="flex items-center gap-2 rounded-xl border border-[#eadfd4] bg-[#fcf7f2] px-3 py-2">
                      <i class="ri-search-line text-muted"></i>
                      <input
                        v-model.trim="partnerCategorySearchQuery"
                        type="text"
                        placeholder="Search category"
                        class="w-full bg-transparent text-sm normal-case text-ink outline-none placeholder:text-muted"
                      >
                    </div>
                  </div>

                  <div class="max-h-64 overflow-y-auto p-2">
                    <button
                      v-for="item in filteredPartnerCategoryOptions"
                      :key="item.id"
                      type="button"
                      class="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm normal-case transition"
                      :class="partnerForm.category === item.id ? 'bg-[#fbf1e6] font-semibold text-brand' : 'text-ink hover:bg-[#fcf7f2]'"
                      @click="selectPartnerCategory(item)"
                    >
                      <span>{{ item.name }}</span>
                      <i v-if="partnerForm.category === item.id" class="ri-check-line text-brand"></i>
                    </button>

                    <p v-if="!filteredPartnerCategoryOptions.length" class="px-3 py-3 text-sm normal-case text-muted">
                      No categories found.
                    </p>
                  </div>
                </div>
              </div>
              <p v-if="partnerFieldErrors.category" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ partnerFieldErrors.category }}</p>
            </label>
            <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
              Phone
              <div class="mt-2 grid gap-3 md:grid-cols-[minmax(0,220px)_1fr]">
                <select v-model="partnerForm.phoneCode" class="rounded-2xl border border-[#e8ddd1] bg-white px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                  <option v-for="country in countries" :key="`partner-${country.code}-${country.phoneCode}`" :value="country.phoneCode">
                    {{ country.flag }} {{ country.name }} ({{ country.phoneCode }})
                  </option>
                </select>
                <input
                  v-model.trim="partnerForm.phone"
                  type="tel"
                  inputmode="numeric"
                  maxlength="10"
                  placeholder="e.g. 9876543210"
                  class="rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none"
                  @input="syncPartnerPhone"
                  @blur="syncPartnerPhone"
                >
              </div>
              <p v-if="partnerFieldErrors.phone" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ partnerFieldErrors.phone }}</p>
            </label>
          </div>

          <div class="mt-6 flex flex-wrap gap-3">
            <button type="button" class="rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark" @click="savePartner">
              {{ saving ? 'Saving...' : isEditingPartnerRoute ? 'Update partner' : 'Save partner' }}
            </button>
            <button type="button" class="rounded-xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand" @click="closePartnerForm">
              Cancel
            </button>
          </div>
        </section>
      </div>
    </div>

    <div v-else-if="isVendorPackageEditorRoute" class="px-8 py-8 lg:px-9">
      <div class="mx-auto max-w-5xl space-y-6">
        <div class="flex flex-wrap items-start justify-between gap-4 rounded-[24px] border border-[#eadfd4] bg-[#fdf8f3] px-6 py-5">
          <div>
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Service package</p>
            <h3 class="mt-2 font-serif text-[2rem] font-semibold text-ink">{{ packageEditorHeading }}</h3>
            <p class="mt-2 max-w-3xl text-sm text-muted">Create a package customers can compare and inquire about from your public listing.</p>
          </div>
          <button
            type="button"
            class="rounded-xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand"
            @click="closePackageForm"
          >
            Back to pricing
          </button>
        </div>

        <div v-if="!moduleId" class="rounded-2xl border border-dashed border-[#eadfd4] bg-white p-5 text-sm text-muted">
          Save the vendor identity first before adding packages.
        </div>

        <section v-else class="rounded-[24px] border border-dashed border-brand bg-white p-5">
          <h3 class="font-serif text-[1.75rem] font-semibold text-brand">{{ packageEditorHeading }}</h3>
          <div class="mt-5 grid gap-4 md:grid-cols-2">
            <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
              Package name
              <input v-model.trim="packageForm.name" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              <p v-if="packageFieldErrors.name" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ packageFieldErrors.name }}</p>
            </label>
            <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
              Price (₹)
              <input v-model.trim="packageForm.price" type="text" inputmode="numeric" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              <p v-if="packageFieldErrors.price" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ packageFieldErrors.price }}</p>
            </label>
            <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638] md:col-span-2">
              What's included
              <textarea v-model.trim="packageForm.description" rows="4" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none"></textarea>
              <p v-if="packageFieldErrors.description" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ packageFieldErrors.description }}</p>
            </label>
          </div>

          <div class="mt-6">
            <p class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Includes tags</p>
            <div class="mt-3 flex flex-wrap gap-2">
              <button
                v-for="item in vendorPackageTagOptions"
                :key="item"
                type="button"
                class="rounded-full border px-3 py-1.5 text-sm transition"
                :class="packageForm.includedServices.includes(item) ? 'border-brand bg-[#fbf1e6] text-brand' : 'border-[#e5d8cc] bg-white text-muted hover:border-brand hover:text-brand'"
                @click="toggleTag(packageForm.includedServices, item)"
              >
                {{ item }}
              </button>
            </div>
            <p v-if="packageFieldErrors.includedServices" class="mt-3 text-xs font-semibold normal-case text-red-600">{{ packageFieldErrors.includedServices }}</p>
          </div>

          <div class="mt-6 grid gap-4 md:grid-cols-2">
            <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
              Max guests
              <input v-model.trim="packageForm.maxGuests" type="text" inputmode="numeric" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              <p v-if="packageFieldErrors.maxGuests" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ packageFieldErrors.maxGuests }}</p>
            </label>
            <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
              Valid for
              <input v-model.trim="packageForm.validHours" type="text" placeholder="e.g. 8 hrs" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              <p v-if="packageFieldErrors.validHours" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ packageFieldErrors.validHours }}</p>
            </label>
          </div>

          <div class="mt-6 flex flex-wrap gap-3">
            <button type="button" class="rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark" @click="savePackage">
              {{ saving ? 'Saving...' : isEditingPackageRoute ? 'Update package' : 'Add package' }}
            </button>
            <button type="button" class="rounded-xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand" @click="closePackageForm">
              Cancel
            </button>
          </div>
        </section>
      </div>
    </div>

    <div v-else-if="isVenuePackageEditorRoute" class="px-8 py-8 lg:px-9">
      <div class="mx-auto max-w-5xl space-y-6">
        <div class="flex flex-wrap items-start justify-between gap-4 rounded-[24px] border border-[#eadfd4] bg-[#fdf8f3] px-6 py-5">
          <div>
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Venue package</p>
            <h3 class="mt-2 font-serif text-[2rem] font-semibold text-ink">{{ packageEditorHeading }}</h3>
            <p class="mt-2 max-w-3xl text-sm text-muted">Packages are venue-level offers with pricing, inclusions, guest cap, and validity hours.</p>
          </div>
          <button
            type="button"
            class="rounded-xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand"
            @click="closePackageForm"
          >
            Back to packages
          </button>
        </div>

        <div v-if="!currentVenueId" class="rounded-2xl border border-dashed border-[#eadfd4] bg-white p-5 text-sm text-muted">
          Save the venue identity first before adding packages.
        </div>

        <section v-else class="rounded-[24px] border border-dashed border-brand bg-white p-5">
          <h3 class="font-serif text-[1.75rem] font-semibold text-brand">{{ packageEditorHeading }}</h3>
          <div class="mt-5 grid gap-4 md:grid-cols-2">
            <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
              Package name
              <input v-model.trim="packageForm.name" type="text" placeholder="e.g. Wedding Gold Package" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              <p v-if="packageFieldErrors.name" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ packageFieldErrors.name }}</p>
            </label>
            <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
              Price
              <input v-model.trim="packageForm.price" type="text" placeholder="e.g. 250000" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              <span class="mt-2 block text-xs font-normal normal-case text-muted">Enter only the number if your backend expects a raw amount.</span>
              <p v-if="packageFieldErrors.price" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ packageFieldErrors.price }}</p>
            </label>
            <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
              Max guests
              <input v-model.trim="packageForm.maxGuests" type="text" placeholder="e.g. 300" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              <p v-if="packageFieldErrors.maxGuests" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ packageFieldErrors.maxGuests }}</p>
            </label>
            <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
              Valid hours
              <input v-model.trim="packageForm.validHours" type="text" placeholder="e.g. 6 hours" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              <p v-if="packageFieldErrors.validHours" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ packageFieldErrors.validHours }}</p>
            </label>
            <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638] md:col-span-2">
              Description
              <textarea v-model.trim="packageForm.description" rows="4" placeholder="e.g. Ideal for day weddings with stage setup, standard lighting, and dining area access." class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none"></textarea>
              <p v-if="packageFieldErrors.description" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ packageFieldErrors.description }}</p>
            </label>
          </div>

          <div class="mt-6">
            <p class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Included services</p>
            <div class="mt-3 flex flex-wrap gap-2">
              <button
                v-for="item in venueInHouseServiceOptions"
                :key="item"
                type="button"
                class="rounded-full border px-3 py-1.5 text-sm transition"
                :class="packageForm.includedServices.includes(item) ? 'border-brand bg-[#fbf1e6] text-brand' : 'border-[#e5d8cc] bg-white text-muted hover:border-brand hover:text-brand'"
                @click="toggleTag(packageForm.includedServices, item)"
              >
                {{ item }}
              </button>
            </div>
            <p class="mt-3 text-sm text-muted">Loaded from the in-house services master list.</p>
            <p v-if="packageFieldErrors.includedServices" class="mt-3 text-xs font-semibold normal-case text-red-600">{{ packageFieldErrors.includedServices }}</p>
          </div>

          <div class="mt-6 flex flex-wrap gap-3">
            <button type="button" class="rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark" @click="savePackage">
              {{ saving ? 'Saving...' : isEditingPackageRoute ? 'Update package' : 'Save package' }}
            </button>
            <button type="button" class="rounded-xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand" @click="closePackageForm">
              Cancel
            </button>
          </div>
        </section>
      </div>
    </div>

    <div v-else-if="isSpaceEditorRoute" class="px-8 py-8 lg:px-9">
      <div class="mx-auto max-w-5xl space-y-6">
        <div class="flex flex-wrap items-start justify-between gap-4 rounded-[24px] border border-[#eadfd4] bg-[#fdf8f3] px-6 py-5">
          <div>
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Venue space</p>
            <h3 class="mt-2 font-serif text-[2rem] font-semibold text-ink">{{ spaceEditorHeading }}</h3>
            <p class="mt-2 max-w-3xl text-sm text-muted">Each space is a separately bookable area within your venue. Configure hall, lawn, or terrace details here.</p>
          </div>
          <button
            type="button"
            class="rounded-xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand"
            @click="closeSpaceForm"
          >
            Back to spaces
          </button>
        </div>

        <div v-if="!currentVenueId" class="rounded-2xl border border-dashed border-[#eadfd4] bg-white p-5 text-sm text-muted">
          Save the venue identity first before adding spaces.
        </div>

        <section v-else class="rounded-[24px] border border-dashed border-brand bg-white p-5">
          <h3 class="font-serif text-[1.75rem] font-semibold text-brand">{{ spaceEditorHeading }}</h3>

          <div class="mt-5 grid gap-4 md:grid-cols-2">
            <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
              Space name <span class="text-brand">*</span>
              <input v-model.trim="spaceForm.name" type="text" placeholder="e.g. Emerald Hall" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              <p v-if="spaceFieldErrors.name" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ spaceFieldErrors.name }}</p>
            </label>
            <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
              Space type
              <select v-model="spaceForm.spaceType" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                <option value="">Select...</option>
                <option v-for="item in venueSpaceTypeOptions" :key="item.id" :value="item.id">{{ item.name }}</option>
              </select>
              <p v-if="spaceFieldErrors.spaceType" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ spaceFieldErrors.spaceType }}</p>
            </label>
          </div>

          <div class="mt-5">
            <p class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Setting</p>
            <div class="mt-3 grid gap-3 md:grid-cols-3">
              <button
                v-for="option in spaceSettingOptions"
                :key="option"
                type="button"
                class="rounded-2xl border px-4 py-3 text-sm font-medium transition"
                :class="spaceForm.setting === option ? 'border-brand bg-[#fbf1e6] text-brand' : 'border-[#e5d8cc] bg-white text-muted hover:border-brand hover:text-brand'"
                @click="spaceForm.setting = option"
              >
                {{ option }}
              </button>
            </div>
          </div>

          <div class="mt-5 grid gap-4 md:grid-cols-2">
            <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
              Area (sqft)
              <input v-model.trim="spaceForm.areaSqft" type="text" placeholder="e.g. 4500" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              <p v-if="spaceFieldErrors.areaSqft" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ spaceFieldErrors.areaSqft }}</p>
            </label>
            <div class="rounded-2xl border border-[#eadfd4] bg-[#fcf7f2] px-4 py-4 text-sm text-muted">
              Capacity is expected through the selected seat configurations for this endpoint.
            </div>
          </div>

          <div class="mt-6">
            <p class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Seating configurations & capacities</p>
            <div class="mt-3 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-4 text-sm text-sky-800">
              Tick each setup your space supports and enter the capacity for that arrangement. Customers filter by configuration.
            </div>
            <div class="mt-4 grid gap-3 md:grid-cols-2">
              <label
                v-for="option in venueSpaceSeatConfigOptions"
                :key="option"
                class="flex cursor-pointer items-center gap-3 rounded-2xl border border-[#eadfd4] bg-white px-4 py-4 text-sm text-ink"
              >
                <input v-model="spaceForm.seatingConfigurations" type="checkbox" class="h-4 w-4 accent-brand" :value="option" @change="handleSeatConfigToggle(option)">
                <span class="flex-1">{{ option }}</span>
                <input
                  v-if="spaceForm.seatingConfigurations.includes(option)"
                  v-model.trim="spaceForm.seatingCapacities[option]"
                  type="text"
                  inputmode="numeric"
                  maxlength="6"
                  placeholder="Capacity"
                  class="w-[108px] rounded-xl border border-[#e8ddd1] px-3 py-2 text-xs normal-case font-normal focus:border-brand focus:outline-none"
                  @input="syncSeatConfigCapacity(option)"
                >
              </label>
            </div>
            <p v-if="spaceFieldErrors.seatingConfigurations" class="mt-3 text-xs font-semibold normal-case text-red-600">{{ spaceFieldErrors.seatingConfigurations }}</p>
          </div>

          <div class="mt-6 grid gap-4 md:grid-cols-2">
            <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
              Min price (₹)
              <input v-model.trim="spaceForm.minPrice" type="text" placeholder="e.g. 50000" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              <p v-if="spaceFieldErrors.minPrice" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ spaceFieldErrors.minPrice }}</p>
            </label>
            <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
              Max price (₹)
              <input v-model.trim="spaceForm.maxPrice" type="text" placeholder="e.g. 120000" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
              <p v-if="spaceFieldErrors.maxPrice" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ spaceFieldErrors.maxPrice }}</p>
            </label>
          </div>

          <div class="mt-6">
            <p class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Space amenities (permanent / included)</p>
            <div class="mt-3 flex flex-wrap gap-2">
              <button
                v-for="item in venueAmenitiesOptions"
                :key="item"
                type="button"
                class="rounded-full border px-3 py-1.5 text-sm transition"
                :class="spaceForm.amenities.includes(item) ? 'border-brand bg-[#fbf1e6] text-brand' : 'border-[#e5d8cc] bg-white text-muted hover:border-brand hover:text-brand'"
                @click="toggleTag(spaceForm.amenities, item)"
              >
                {{ item }}
              </button>
            </div>
            <p class="mt-3 text-sm text-muted">Features this specific space has, not equipment that's brought in</p>
          </div>

          <label class="mt-6 block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
            Space description
            <textarea v-model.trim="spaceForm.description" rows="4" placeholder="e.g. Pillarless indoor hall suitable for weddings, receptions, and corporate banquets." class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none"></textarea>
            <p v-if="spaceFieldErrors.description" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ spaceFieldErrors.description }}</p>
          </label>

          <div class="mt-6 flex flex-wrap gap-3">
            <button type="button" class="rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark disabled:opacity-60" :disabled="saving" @click="saveSpace">
              {{ saving ? 'Saving...' : isEditingSpaceRoute ? 'Update space' : 'Save space' }}
            </button>
            <button type="button" class="rounded-xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand" @click="closeSpaceForm">
              Cancel
            </button>
          </div>
        </section>
      </div>
    </div>

    <div v-else class="grid min-h-[760px] bg-[#fdf8f3] lg:grid-cols-[210px_1fr]">
      <aside class="border-r border-[#eee4da] bg-white px-0 py-3">
        <button
          v-for="item in sections"
          :key="item.id"
          type="button"
          class="flex w-full items-center justify-between px-6 py-4 text-left text-[1.02rem] transition"
          :class="activeSection === item.id ? 'border-r-[3px] border-brand bg-[#fbf1e6] font-semibold text-brand' : 'text-ink hover:bg-[#fcf5ed]'"
          @click="activeSection = item.id"
        >
          <span>{{ item.label }}</span>
        </button>
      </aside>

      <div class="px-8 py-8 lg:px-9">
        <div v-if="moduleType === 'venue'" class="space-y-6">
          <template v-if="activeSection === 'overview'">
            <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
              <div class="flex flex-wrap items-end gap-3">
                <span class="font-serif text-[4rem] font-semibold leading-none text-ink">{{ venueCompletion }}%</span>
                <span class="pb-3 text-2xl text-muted">complete</span>
              </div>
              <div class="mt-5 h-2 w-full rounded-full bg-[#f5ebe2]">
                <div class="h-2 rounded-full bg-[#c73d3d] transition-all" :style="{ width: `${venueCompletion}%` }"></div>
              </div>

              <div class="mt-6 grid gap-3 md:grid-cols-2">
                <div
                  v-for="item in venueOverviewChecklist"
                  :key="item.label"
                  class="flex items-center gap-3 rounded-2xl border px-4 py-4"
                  :class="item.complete ? 'border-emerald-200 bg-emerald-50' : 'border-[#eadfd4] bg-white'"
                >
                  <span
                    class="flex h-7 w-7 items-center justify-center rounded-full text-sm"
                    :class="item.complete ? 'bg-emerald-700 text-white' : 'bg-[#d8ccc2] text-white'"
                  >
                    <i :class="item.complete ? 'ri-check-line' : 'ri-error-warning-line'"></i>
                  </span>
                  <span class="text-[1.02rem] font-medium" :class="item.complete ? 'text-emerald-800' : 'text-ink'">{{ item.label }}</span>
                </div>
              </div>

              <div class="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#f0e4d8] bg-[#fdf8f3] px-4 py-4">
                <p class="text-sm text-[#6f5a48]">
                  {{ canPublishVenue ? 'Venue is ready to publish.' : 'Complete all overview checklist items to enable publishing.' }}
                </p>
                <button
                  type="button"
                  class="rounded-xl px-5 py-3 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60"
                  :class="canPublishVenue ? 'bg-brand hover:bg-brandDark' : 'bg-[#d8c8b9]'"
                  :disabled="!canPublishVenue || publishingVenue"
                  @click="publishVenueOverview"
                >
                  {{ publishingVenue ? 'Publishing...' : 'Publish venue' }}
                </button>
              </div>
            </section>

            <section class="rounded-[24px] border border-[#f2e1d5] bg-[#fff2ea] p-6">
              <p class="text-xs font-semibold uppercase tracking-wide text-[#b78969]">Under business</p>
              <p class="mt-2 text-[1.35rem] font-medium text-[#6b4d39]">{{ businessName }}</p>
            </section>
          </template>

          <template v-else-if="activeSection === 'identity'">
            <div class="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800">
              This is what customers see on the marketplace. Your legal business name stays private.
            </div>

            <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
              <h3 class="font-serif text-[2rem] font-semibold text-ink">Venue identity</h3>
              <div class="mt-5 grid gap-4">
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  Venue name (public) <span class="text-brand">*</span>
                  <input v-model.trim="venueForm.title" type="text" placeholder="e.g. Royal Palm Banquet" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                  <p v-if="venueFieldErrors.title" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ venueFieldErrors.title }}</p>
                </label>
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  Venue type
                  <select
                    :value="venueForm.type"
                    class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none"
                    @change="setVenueType($event.target.value)"
                  >
                    <option value="">Select...</option>
                    <option v-for="item in venueTypeOptions" :key="item.id" :value="item.id">{{ item.name }}</option>
                  </select>
                  <p v-if="venueFieldErrors.type" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ venueFieldErrors.type }}</p>
                </label>
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  Tagline / one-line bio <span class="text-brand">*</span>
                  <input v-model.trim="venueForm.tagLine" type="text" placeholder="e.g. Elegant indoor venue for weddings and receptions" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                  <p v-if="venueFieldErrors.tagLine" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ venueFieldErrors.tagLine }}</p>
                </label>
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  About this venue
                  <textarea v-model.trim="venueForm.about" rows="5" placeholder="e.g. A premium banquet venue with valet parking, bridal room, and in-house catering for weddings up to 800 guests." class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none"></textarea>
                </label>
              </div>
            </section>

            <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
              <h3 class="font-serif text-[2rem] font-semibold text-ink">Location</h3>
              <div class="mt-5 grid gap-4 md:grid-cols-3">
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638] md:col-span-3">
                  Address
                  <div class="mt-2 flex flex-col gap-3 md:flex-row">
                    <div class="flex-1">
                      <input v-model.trim="venueForm.address" type="text" placeholder="e.g. 24 MG Road, Indiranagar, Bengaluru, Karnataka" class="w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                    </div>
                    <button
                      type="button"
                      class="inline-flex items-center justify-center gap-2 rounded-2xl border border-[#e5d8cc] bg-[#fcf7f2] px-5 py-3 text-sm font-semibold normal-case text-ink transition hover:border-brand hover:text-brand"
                      @click="openLocationPicker"
                    >
                      <i class="ri-map-pin-2-line"></i>
                      Choose on map
                    </button>
                  </div>
                  <span class="mt-2 block text-xs font-normal normal-case text-muted">Open the map to search, drag the pin, or use current location. Address, latitude, and longitude will fill automatically.</span>
                  <p v-if="venueFieldErrors.address" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ venueFieldErrors.address }}</p>
                </label>
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  Latitude
                  <input v-model.trim="venueForm.latitude" type="text" placeholder="e.g. 12.9716" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                  <span class="mt-2 block text-xs font-normal normal-case text-muted">Decimal format from Google Maps works best.</span>
                  <p v-if="venueFieldErrors.latitude" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ venueFieldErrors.latitude }}</p>
                </label>
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  Longitude
                  <input v-model.trim="venueForm.longitude" type="text" placeholder="e.g. 77.5946" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                  <span class="mt-2 block text-xs font-normal normal-case text-muted">Decimal format from Google Maps works best.</span>
                  <p v-if="venueFieldErrors.longitude" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ venueFieldErrors.longitude }}</p>
                </label>
              </div>
            </section>

            <div v-if="locationPickerOpen" class="fixed inset-0 z-[120] flex items-center justify-center bg-black/55 px-4 py-6">
              <div class="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-[28px] border border-[#eadfd4] bg-white shadow-2xl">
                <div class="flex flex-wrap items-center justify-between gap-3 border-b border-[#eee4da] px-6 py-5">
                  <div>
                    <h4 class="text-[1.45rem] font-semibold text-ink">Choose venue location</h4>
                    <p class="mt-1 text-sm text-muted">Search an address, drag the marker, click on the map, or use your current location.</p>
                  </div>
                  <button type="button" class="rounded-full border border-[#e5d8cc] px-3 py-2 text-sm text-muted transition hover:border-brand hover:text-brand" @click="closeLocationPicker">
                    <i class="ri-close-line text-lg"></i>
                  </button>
                </div>

                <div class="grid gap-5 p-6 lg:grid-cols-[320px_minmax(0,1fr)]">
                  <div class="space-y-4">
                    <div class="rounded-[22px] border border-[#eadfd4] bg-[#fcf7f2] p-4">
                      <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                        Search location
                        <div class="mt-2 flex gap-2">
                          <input
                            v-model.trim="locationSearchQuery"
                            type="text"
                            placeholder="Search address, area, or landmark"
                            class="w-full rounded-2xl border border-[#e8ddd1] bg-white px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none"
                            @keydown.enter.prevent="searchLocation"
                          >
                          <button
                            type="button"
                            class="rounded-2xl bg-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-brandDark disabled:opacity-60"
                            :disabled="locationSearchLoading"
                            @click="searchLocation"
                          >
                            {{ locationSearchLoading ? '...' : 'Search' }}
                          </button>
                        </div>
                      </label>
                      <button
                        type="button"
                        class="mt-3 inline-flex items-center gap-2 rounded-2xl border border-[#e5d8cc] bg-white px-4 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand disabled:opacity-60"
                        :disabled="locationCurrentLoading"
                        @click="loadCurrentLocationIntoPicker(true)"
                      >
                        <i class="ri-navigation-line"></i>
                        {{ locationCurrentLoading ? 'Detecting...' : 'Use current location' }}
                      </button>
                      <p v-if="locationPickerError" class="mt-3 text-xs font-semibold normal-case text-red-600">{{ locationPickerError }}</p>
                    </div>

                    <div class="rounded-[22px] border border-[#eadfd4] bg-white p-4">
                      <p class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Search results</p>
                      <div class="mt-3 max-h-[220px] space-y-2 overflow-y-auto">
                        <button
                          v-for="(item, index) in locationSearchResults"
                          :key="`${item.placeId || item.label}-${index}`"
                          type="button"
                          class="block w-full rounded-2xl border border-[#eadfd4] px-4 py-3 text-left text-sm text-ink transition hover:border-brand hover:bg-[#fbf1e6]"
                          @click="applySearchedLocation(item)"
                        >
                          <span class="block font-semibold">{{ item.label }}</span>
                          <span class="mt-1 block text-xs text-muted">{{ item.meta }}</span>
                        </button>
                        <p v-if="!locationSearchLoading && !locationSearchResults.length" class="rounded-2xl border border-dashed border-[#eadfd4] px-4 py-4 text-sm text-muted">
                          Search for an address or use current location.
                        </p>
                      </div>
                    </div>

                    <div class="rounded-[22px] border border-[#eadfd4] bg-white p-4">
                      <p class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Selected location</p>
                      <p class="mt-3 text-sm font-medium text-ink">{{ locationPickerAddress || 'No address selected yet' }}</p>
                      <div class="mt-3 grid gap-3 sm:grid-cols-2">
                        <div class="rounded-2xl bg-[#fcf7f2] px-4 py-3 text-sm text-ink">
                          <span class="block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Latitude</span>
                          <span class="mt-1 block">{{ locationPickerLat || '-' }}</span>
                        </div>
                        <div class="rounded-2xl bg-[#fcf7f2] px-4 py-3 text-sm text-ink">
                          <span class="block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Longitude</span>
                          <span class="mt-1 block">{{ locationPickerLng || '-' }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="space-y-4">
                    <div id="venue-location-picker-map" class="h-[420px] overflow-hidden rounded-[24px] border border-[#eadfd4] bg-[#f5f1eb]"></div>
                    <div class="flex flex-wrap justify-end gap-3">
                      <button type="button" class="rounded-xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand" @click="closeLocationPicker">
                        Cancel
                      </button>
                      <button type="button" class="rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark" @click="confirmLocationPicker">
                        Use this location
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
              <h3 class="font-serif text-[2rem] font-semibold text-ink">Contact & social</h3>
              <div class="mt-5 grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
                <div class="rounded-[22px] border border-[#eadfd4] bg-[#fcf7f2] p-5">
                  <div class="flex items-center gap-3">
                    <span class="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-brand ring-1 ring-[#eadfd4]">
                      <i class="ri-phone-line text-lg"></i>
                    </span>
                    <div>
                      <p class="text-sm font-semibold text-ink">Direct contact</p>
                      <p class="text-sm text-muted">Primary details shown to customers for enquiries and booking.</p>
                    </div>
                  </div>

                  <div class="mt-5 grid gap-4 md:grid-cols-2">
                    <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638] md:col-span-2">
                      Venue phone
                      <div class="mt-2 grid gap-3 lg:grid-cols-[220px_minmax(0,1fr)]">
                        <select v-model="venueForm.phoneCode" class="rounded-2xl border border-[#e8ddd1] bg-white px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                          <option v-for="country in countries" :key="`${country.code}-${country.phoneCode}`" :value="country.phoneCode">
                            {{ country.flag }} {{ country.name }} ({{ country.phoneCode }})
                          </option>
                        </select>
                        <input v-model.trim="venueForm.phone" type="tel" inputmode="numeric" placeholder="e.g. 9876543210" class="rounded-2xl border border-[#e8ddd1] bg-white px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none" @input="syncVenuePhone" @blur="syncVenuePhone">
                      </div>
                      <span class="mt-2 block text-xs font-normal normal-case text-muted">Default country code is `+91`.</span>
                      <p v-if="venueFieldErrors.phone" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ venueFieldErrors.phone }}</p>
                    </label>
                    <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                      Venue email
                      <input v-model.trim="venueForm.email" type="email" placeholder="e.g. bookings@royalpalm.com" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] bg-white px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                      <p v-if="venueFieldErrors.email" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ venueFieldErrors.email }}</p>
                    </label>
                    <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                      Website
                      <input v-model.trim="venueForm.website" type="text" placeholder="e.g. https://royalpalmvenues.com" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] bg-white px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                      <p v-if="venueFieldErrors.website" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ venueFieldErrors.website }}</p>
                    </label>
                  </div>
                </div>

                <div class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] p-5">
                  <div class="flex items-center gap-3">
                    <span class="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-brand ring-1 ring-[#eadfd4]">
                      <i class="ri-share-forward-line text-lg"></i>
                    </span>
                    <div>
                      <p class="text-sm font-semibold text-ink">Social profiles</p>
                      <p class="text-sm text-muted">Add public profile URLs or handles customers can verify.</p>
                    </div>
                  </div>

                  <div class="mt-5 grid gap-4">
                    <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                      Instagram
                      <input v-model.trim="venueForm.instagram" type="text" placeholder="e.g. @royalpalmvenue or instagram.com/royalpalmvenue" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] bg-white px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                    </label>
                    <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                      Facebook
                      <input v-model.trim="venueForm.facebook" type="text" placeholder="e.g. facebook.com/royalpalmvenue" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] bg-white px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                    </label>
                    <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                      YouTube
                      <input v-model.trim="venueForm.youtube" type="text" placeholder="e.g. youtube.com/@royalpalmvenue" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] bg-white px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                    </label>
                  </div>
                </div>
              </div>
              <div class="mt-6">
                <p class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Venue highlights / tags</p>
                <div class="mt-3 flex flex-wrap gap-2">
                  <button
                    v-for="tag in venueHighlightOptions"
                    :key="tag"
                    type="button"
                    class="rounded-full border px-3 py-1.5 text-xs font-medium transition"
                    :class="venueForm.highlights.includes(tag) ? 'border-brand bg-[#fbf1e6] text-brand' : 'border-[#e5d8cc] bg-white text-muted hover:border-brand hover:text-brand'"
                    @click="toggleTag(venueForm.highlights, tag)"
                  >
                    {{ tag }}
                  </button>
                </div>
              </div>
            </section>

            <div class="flex flex-wrap justify-end gap-3">
              <button
                type="button"
                class="rounded-xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="saving"
                @click="saveModule(true)"
              >
                {{ saving ? 'Saving...' : 'Save & exit' }}
              </button>
              <button
                type="button"
                class="rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="saving"
                @click="saveModule(false)"
              >
                {{ saving ? 'Saving...' : 'Save venue identity' }}
              </button>
            </div>
          </template>

          <template v-else-if="activeSection === 'details'">
            <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
              <h3 class="font-serif text-[2rem] font-semibold text-ink">Venue classification</h3>
              <div class="mt-5 grid gap-4 md:grid-cols-2">
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  Venue type
                  <select
                    :value="venueDetailsForm.venueType"
                    class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none"
                    @change="setVenueType($event.target.value)"
                  >
                    <option value="">Select...</option>
                    <option v-for="item in venueTypeOptions" :key="item.id" :value="item.id">{{ item.name }}</option>
                  </select>
                  <p v-if="venueDetailsErrors.venueType" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ venueDetailsErrors.venueType }}</p>
                </label>
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  Year established
                  <input v-model.trim="venueDetailsForm.yearEstablished" type="text" placeholder="e.g. 2005" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                  <p v-if="venueDetailsErrors.yearEstablished" class="mt-2 text-xs font-semibold normal-case text-red-600">{{ venueDetailsErrors.yearEstablished }}</p>
                </label>
              </div>

              <div class="mt-5">
                <p class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Events hosted</p>
                <div class="mt-3 flex flex-wrap gap-2">
                  <button
                    v-for="item in venueEventsHostedOptions"
                    :key="item"
                    type="button"
                    class="rounded-full border px-3 py-1.5 text-sm transition"
                    :class="venueDetailsForm.eventsHosted.includes(item) ? 'border-brand bg-[#fbf1e6] text-brand' : 'border-[#e5d8cc] bg-white text-muted hover:border-brand hover:text-brand'"
                    @click="toggleTag(venueDetailsForm.eventsHosted, item)"
                  >
                    {{ item }}
                  </button>
                </div>
                <p class="mt-3 text-sm text-muted">Which event types does this venue cater to?</p>
                <p v-if="venueDetailsErrors.eventsHosted" class="mt-3 text-xs font-semibold normal-case text-red-600">{{ venueDetailsErrors.eventsHosted }}</p>
              </div>
            </section>

            <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
              <h3 class="font-serif text-[2rem] font-semibold text-ink">Venue-level amenities</h3>
              <p class="mt-2 text-sm text-muted">Permanent features of the property, not space-specific equipment.</p>
              <div class="mt-5">
                <p class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Property amenities</p>
                <div class="mt-3 flex flex-wrap gap-2">
                  <button
                    v-for="item in venueAmenitiesOptions"
                    :key="item"
                    type="button"
                    class="rounded-full border px-3 py-1.5 text-sm transition"
                    :class="venueDetailsForm.amenities.includes(item) ? 'border-brand bg-[#fbf1e6] text-brand' : 'border-[#e5d8cc] bg-white text-muted hover:border-brand hover:text-brand'"
                    @click="toggleTag(venueDetailsForm.amenities, item)"
                  >
                    {{ item }}
                  </button>
                </div>
              </div>
            </section>

            <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
              <h3 class="font-serif text-[2rem] font-semibold text-ink">In-house services</h3>
              <p class="mt-2 text-sm text-muted">Which services does your venue provide in-house? These are managed and operated by your venue team.</p>
              <div class="mt-5">
                <p class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">In-house services offered</p>
                <div class="mt-3 flex flex-wrap gap-2">
                  <button
                    v-for="item in venueInHouseServiceOptions"
                    :key="item"
                    type="button"
                    class="rounded-full border px-3 py-1.5 text-sm transition"
                    :class="venueDetailsForm.inHouseServices.includes(item) ? 'border-brand bg-[#fbf1e6] text-brand' : 'border-[#e5d8cc] bg-white text-muted hover:border-brand hover:text-brand'"
                    @click="toggleTag(venueDetailsForm.inHouseServices, item)"
                  >
                    {{ item }}
                  </button>
                </div>
                <p class="mt-3 text-sm text-muted">Select all that your venue provides, appears on the listing as included services.</p>
              </div>
            </section>

            <div class="flex flex-wrap justify-end gap-3">
              <button
                type="button"
                class="rounded-xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="saving"
                @click="saveVenueDetailsSection(true)"
              >
                {{ saving ? 'Saving...' : 'Save & exit' }}
              </button>
              <button
                type="button"
                class="rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="saving"
                @click="saveVenueDetailsSection(false)"
              >
                {{ saving ? 'Saving...' : 'Save venue details' }}
              </button>
            </div>
          </template>

          <template v-else-if="activeSection === 'packages'">
            <div class="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-4 text-sm text-sky-800">
              Packages are venue-level offers with pricing, inclusions, guest cap, and validity hours.
            </div>

            <button
              type="button"
              class="rounded-2xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand"
              :disabled="!currentVenueId"
              @click="openPackageCreatePage"
            >
              + Add package
            </button>

            <div v-if="!currentVenueId" class="rounded-2xl border border-dashed border-[#eadfd4] bg-white p-5 text-sm text-muted">
              Save the venue identity first before adding packages.
            </div>

            <div v-if="venuePackages.length" class="grid gap-4 md:grid-cols-2">
              <article v-for="pkg in venuePackages" :key="pkg.id" class="rounded-[22px] border border-[#eadfd4] bg-white p-5">
                <div class="flex items-start justify-between gap-4">
                  <div>
                    <p class="text-[1.35rem] font-semibold text-ink">{{ pkg.name }}</p>
                    <div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted">
                      <span>₹{{ pkg.price || '0' }}</span>
                      <span class="inline-flex items-center gap-1.5">
                        <i class="ri-group-line text-[0.95rem]"></i>
                        {{ pkg.maxGuests || 'N/A' }} guests
                      </span>
                      <span class="inline-flex items-center gap-1.5">
                        <i class="ri-time-line text-[0.95rem]"></i>
                        {{ pkg.validHours || 'N/A' }}
                      </span>
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    <button
                      type="button"
                      class="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#e5d8cc] text-brand transition hover:border-brand hover:bg-[#fbf1e6]"
                      @click="editPackage(pkg.id)"
                    >
                      <i class="ri-pencil-line"></i>
                    </button>
                    <button
                      type="button"
                      class="inline-flex h-9 w-9 items-center justify-center rounded-full border border-red-200 text-red-600 transition hover:border-red-300 hover:bg-red-50 disabled:opacity-60"
                      :disabled="deletingPackageId === String(pkg.id)"
                      @click="deletePackage(pkg.id)"
                    >
                      <i v-if="deletingPackageId !== String(pkg.id)" class="ri-delete-bin-line"></i>
                      <i v-else class="ri-loader-4-line animate-spin"></i>
                    </button>
                  </div>
                </div>
                <p class="mt-4 text-sm text-muted">{{ pkg.description || 'No description added.' }}</p>
                <div class="mt-3">
                  <p class="text-sm font-semibold text-ink">Included services</p>
                  <div v-if="pkg.includedServices.length" class="mt-2 flex flex-wrap items-center gap-2">
                    <span
                      v-for="service in visiblePackageServices(pkg)"
                      :key="`${pkg.id}-${service}`"
                      class="inline-flex rounded-full bg-[#fbf1e6] px-3 py-1 text-xs font-medium text-brand"
                    >
                      {{ service }}
                    </span>
                    <button
                      v-if="hiddenPackageServicesCount(pkg) > 0"
                      type="button"
                      class="inline-flex rounded-full border border-[#e5d8cc] bg-white px-3 py-1 text-xs font-semibold text-[#7b624d] transition hover:border-brand hover:text-brand"
                      @click="togglePackageServices(pkg.id)"
                    >
                      {{ isPackageServicesExpanded(pkg.id) ? 'Show less' : `+${hiddenPackageServicesCount(pkg)} more` }}
                    </button>
                  </div>
                  <p v-else class="mt-2 text-sm text-muted">Not specified</p>
                </div>
              </article>
            </div>

          </template>

          <template v-else-if="activeSection === 'spaces'">
            <div class="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-4 text-sm text-sky-800">
              Each space is a separately bookable area within your venue. Add one per hall, lawn, or terrace. All configurations, equipment, and photo assignments live at the space level.
            </div>

            <button
              type="button"
              class="rounded-2xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand"
              :disabled="!currentVenueId"
              @click="openSpaceCreatePage"
            >
              + Add space
            </button>

            <div v-if="!currentVenueId" class="rounded-2xl border border-dashed border-[#eadfd4] bg-white p-5 text-sm text-muted">
              Save the venue identity first before adding spaces.
            </div>

            <div v-if="venueSpaces.length" class="grid gap-4 md:grid-cols-2">
              <article
                v-for="space in venueSpaces"
                :key="space.id"
                class="rounded-[22px] border border-[#eadfd4] bg-white p-5"
              >
                <div class="flex items-start justify-between gap-4">
                  <div>
                    <p class="text-[1.45rem] font-semibold text-ink">{{ space.name }}</p>
                    <p class="mt-1 text-sm text-muted">{{ space.spaceTypeLabel || getSpaceTypeLabel(space.spaceType) || 'Unspecified type' }} · {{ space.setting }}</p>
                  </div>
                  <div class="flex items-center gap-2">
                    <button
                      type="button"
                      class="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#e5d8cc] text-brand transition hover:border-brand hover:bg-[#fbf1e6]"
                      @click="editSpace(space.id)"
                    >
                      <i class="ri-pencil-line"></i>
                    </button>
                    <button
                      type="button"
                      class="inline-flex h-9 w-9 items-center justify-center rounded-full border border-red-200 text-red-600 transition hover:border-red-300 hover:bg-red-50 disabled:opacity-60"
                      :disabled="deletingSpaceId === String(space.id)"
                      @click="deleteSpace(space.id)"
                    >
                      <i v-if="deletingSpaceId !== String(space.id)" class="ri-delete-bin-line"></i>
                      <i v-else class="ri-loader-4-line animate-spin"></i>
                    </button>
                  </div>
                </div>
                <div class="mt-4 grid gap-3 text-sm text-muted sm:grid-cols-2">
                  <p>Area: {{ space.areaSqft || '0' }} sqft</p>
                  <p>Price: ₹{{ space.minPrice || '0' }} - ₹{{ space.maxPrice || '0' }}</p>
                  <p class="sm:col-span-2">Seat config: {{ space.seatingConfigurations.join(', ') || 'Not set' }}</p>
                </div>
              </article>
            </div>

          </template>

          <template v-else-if="activeSection === 'policies'">
            <div class="space-y-5">
              <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
                <h3 class="font-serif text-[1.95rem] font-semibold text-ink">Catering policy</h3>
                <p class="mt-1 text-sm text-muted">Two independent axes - what you offer in-house, and what you permit from outside.</p>

                <div class="mt-5 grid gap-6 lg:grid-cols-2">
                  <div>
                    <p class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">In-house catering</p>
                    <div class="mt-3 space-y-3">
                      <label
                        v-for="option in cateringInHouseOptions"
                        :key="option.value"
                        class="flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition"
                        :class="venuePoliciesForm.inHouseCatering === option.value ? 'border-brand bg-[#fff6ee]' : 'border-[#eadfd4] bg-white hover:border-brand'"
                      >
                        <input v-model="venuePoliciesForm.inHouseCatering" type="radio" name="in-house-catering" :value="option.value" class="mt-1 h-4 w-4 accent-brand">
                        <span class="text-sm text-ink">
                          <span class="mr-2">{{ option.icon }}</span>{{ option.label }}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <p class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Outside catering policy</p>
                    <div class="mt-3 space-y-3">
                      <label
                        v-for="option in cateringOutsideOptions"
                        :key="option.value"
                        class="flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition"
                        :class="venuePoliciesForm.outsideCatering === option.value ? 'border-brand bg-[#fff6ee]' : 'border-[#eadfd4] bg-white hover:border-brand'"
                      >
                        <input v-model="venuePoliciesForm.outsideCatering" type="radio" name="outside-catering" :value="option.value" class="mt-1 h-4 w-4 accent-brand" @change="syncOutsideCateringFeeState">
                        <span class="text-sm text-ink">
                          <span class="mr-2">{{ option.icon }}</span>{{ option.label }}
                        </span>
                      </label>
                    </div>

                    <div v-if="showOutsideCateringFeePanel" class="mt-5 rounded-[20px] border border-[#eadfd4] bg-[#fffdfa] p-4">
                      <p class="text-sm font-semibold text-[#6a4d39]">Outside catering fee (optional)</p>
                      <div class="mt-4 grid gap-4 md:grid-cols-[190px_minmax(0,1fr)]">
                        <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                          Fee type
                          <select v-model="venuePoliciesForm.outsideCateringFeeType" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] bg-white px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none" @change="syncOutsideCateringFeeState">
                            <option>No fee</option>
                            <option>Per plate charge</option>
                            <option>Flat fee</option>
                          </select>
                        </label>

                        <label v-if="outsideCateringFeeAmountLabel" class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                          {{ outsideCateringFeeAmountLabel }}
                          <input v-model.trim="venuePoliciesForm.outsideCateringFeeValue" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-if="cateringPolicySummary"
                  class="mt-5 rounded-2xl border px-4 py-4 text-sm"
                  :class="cateringPolicySummary.tone"
                >
                  {{ cateringPolicySummary.text }}
                </div>
              </section>

              <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
                <h3 class="font-serif text-[1.95rem] font-semibold text-ink">Decor policy</h3>
                <p class="mt-1 text-xs font-semibold uppercase tracking-wide text-[#5b4638]">What is your policy on decoration?</p>
                <div class="mt-4 grid gap-3 md:grid-cols-2">
                  <label
                    v-for="option in decorPolicyOptions"
                    :key="option"
                    class="flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition"
                    :class="venuePoliciesForm.decorPolicy === option ? 'border-brand bg-[#fff6ee]' : 'border-[#eadfd4] bg-white hover:border-brand'"
                  >
                    <input v-model="venuePoliciesForm.decorPolicy" type="radio" name="decor-policy" :value="option" class="mt-1 h-4 w-4 accent-brand">
                    <span class="text-sm text-ink">{{ option }}</span>
                  </label>
                </div>
              </section>

              <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
                <h3 class="font-serif text-[1.95rem] font-semibold text-ink">DJ / music policy</h3>
                <p class="mt-1 text-xs font-semibold uppercase tracking-wide text-[#5b4638]">What is your policy on DJ / live music?</p>
                <div class="mt-4 space-y-3">
                  <label
                    v-for="option in musicPolicyOptions"
                    :key="option"
                    class="flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition"
                    :class="venuePoliciesForm.musicPolicy === option ? 'border-brand bg-[#fff6ee]' : 'border-[#eadfd4] bg-white hover:border-brand'"
                  >
                    <input v-model="venuePoliciesForm.musicPolicy" type="radio" name="music-policy" :value="option" class="mt-1 h-4 w-4 accent-brand">
                    <span class="text-sm text-ink">{{ option }}</span>
                  </label>
                </div>
              </section>

              <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
                <h3 class="font-serif text-[1.95rem] font-semibold text-ink">Alcohol policy</h3>
                <div class="mt-5 grid gap-4 md:grid-cols-2">
                  <div class="rounded-[20px] border border-[#eadfd4] bg-[#fffdfa] px-5 py-4">
                    <div class="flex items-start justify-between gap-4">
                      <div>
                        <p class="text-[1.05rem] font-semibold text-ink">In-house bar / alcohol available</p>
                        <p class="mt-1 text-sm text-muted">Venue provides licensed bar service</p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        :aria-checked="policyAlcoholInhouseEnabled"
                        class="relative mt-1 h-8 w-12 rounded-full transition"
                        :class="policyAlcoholInhouseEnabled ? 'bg-[#2f7a49]' : 'bg-[#d8c8b9]'"
                        @click="toggleAlcoholPolicy('inhouse')"
                      >
                        <span class="absolute top-1 h-6 w-6 rounded-full bg-white shadow-sm transition" :class="policyAlcoholInhouseEnabled ? 'left-5' : 'left-1'"></span>
                      </button>
                    </div>
                  </div>

                  <div class="rounded-[20px] border border-[#eadfd4] bg-[#fffdfa] px-5 py-4">
                    <div class="flex items-start justify-between gap-4">
                      <div>
                        <p class="text-[1.05rem] font-semibold text-ink">Outside alcohol permitted</p>
                        <p class="mt-1 text-sm text-muted">Guests may bring their own or hire outside bar</p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        :aria-checked="policyAlcoholOutsideEnabled"
                        class="relative mt-1 h-8 w-12 rounded-full transition"
                        :class="policyAlcoholOutsideEnabled ? 'bg-[#2f7a49]' : 'bg-[#d8c8b9]'"
                        @click="toggleAlcoholPolicy('outside')"
                      >
                        <span class="absolute top-1 h-6 w-6 rounded-full bg-white shadow-sm transition" :class="policyAlcoholOutsideEnabled ? 'left-5' : 'left-1'"></span>
                      </button>
                    </div>
                  </div>
                </div>
              </section>

              <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
                <h3 class="font-serif text-[1.95rem] font-semibold text-ink">Guest count & logistics</h3>
                <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                    Minimum guests required
                    <input v-model.trim="venuePoliciesForm.minGuests" type="text" placeholder="e.g. 100" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                    <span class="mt-2 block text-xs font-normal normal-case text-muted">Below this, venue may not host the event.</span>
                  </label>
                  <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                    Maximum guests allowed
                    <input v-model.trim="venuePoliciesForm.maxGuests" type="text" placeholder="e.g. 1200" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                    <span class="mt-2 block text-xs font-normal normal-case text-muted">Overall venue capacity upper limit.</span>
                  </label>
                  <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                    Noise / music curfew
                    <input v-model.trim="venuePoliciesForm.noiseMusic" type="text" placeholder="e.g. 11:00 PM" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                    <span class="mt-2 block text-xs font-normal normal-case text-muted">Latest time music / amplified sound is permitted.</span>
                  </label>
                  <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                    Setup / decoration time
                    <input v-model.trim="venuePoliciesForm.decorTime" type="text" placeholder="e.g. 4 hours before event" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                    <span class="mt-2 block text-xs font-normal normal-case text-muted">How early vendors can enter for setup.</span>
                  </label>
                  <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                    Cleanup / teardown time
                    <input v-model.trim="venuePoliciesForm.cleanTime" type="text" placeholder="e.g. 2 hours after event" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                    <span class="mt-2 block text-xs font-normal normal-case text-muted">Time window available after the event.</span>
                  </label>
                </div>

                <label class="mt-5 block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  Other house rules
                  <textarea v-model.trim="venuePoliciesForm.otherHouseRules" rows="4" placeholder="e.g. No confetti, no open flames, no animals on premises." class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none"></textarea>
                </label>
              </section>

              <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
                <h3 class="font-serif text-[1.95rem] font-semibold text-ink">Cancellation policy</h3>
                <p class="mt-1 text-sm text-muted">Write out the cancellation terms that appear on the listing, and the deposit percentage required to block a date.</p>

                <label class="mt-5 block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  Cancellation T&amp;C (full detail)
                  <textarea v-model.trim="venuePoliciesForm.cancelTerms" rows="5" placeholder="e.g. 50% refund if cancelled 30 days before the event." class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none"></textarea>
                </label>

                <label class="mt-5 block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  Deposit % to block a date
                  <input v-model.trim="venuePoliciesForm.depositPercent" type="text" placeholder="e.g. 25" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                  <span class="mt-2 block text-xs font-normal normal-case text-muted">% of total collected upfront to confirm the booking.</span>
                </label>
              </section>

              <div class="flex justify-end">
                <button type="button" class="rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark disabled:opacity-60" :disabled="saving" @click="savePolicies">
                  {{ saving ? 'Saving...' : 'Save policies' }}
                </button>
              </div>
            </div>
          </template>

          <template v-else-if="activeSection === 'partners'">
            <div class="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-4 text-sm text-sky-800">
              Add preferred vendor partners attached to this venue.
            </div>

            <button
              type="button"
              class="rounded-2xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand"
              :disabled="!currentVenueId"
              @click="openPartnerCreatePage"
            >
              + Add vendor partner
            </button>

            <div v-if="!currentVenueId" class="rounded-2xl border border-dashed border-[#eadfd4] bg-white p-5 text-sm text-muted">
              Save the venue identity first before adding vendor partners.
            </div>

            <div v-if="venuePartners.length" class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <article
                v-for="partner in venuePartners"
                :key="partner.id"
                class="group relative overflow-hidden rounded-[24px] border border-[#eadfd4] bg-[linear-gradient(180deg,#fffdfb_0%,#fff7f1_100%)] p-5 shadow-[0_10px_30px_rgba(102,73,44,0.05)] transition hover:-translate-y-0.5 hover:border-[#d9b594] hover:shadow-[0_18px_40px_rgba(102,73,44,0.12)]"
              >
                <div class="pointer-events-none absolute right-0 top-0 h-24 w-24 rounded-full bg-[radial-gradient(circle_at_center,rgba(199,61,61,0.1),rgba(199,61,61,0)_70%)]"></div>
                <div class="relative flex items-start justify-between gap-4">
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center gap-3">
                      <span class="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-brand shadow-sm ring-1 ring-[#eadfd4]">
                        <i class="ri-hand-heart-line text-lg"></i>
                      </span>
                      <div class="min-w-0">
                        <p class="truncate text-[1.2rem] font-semibold text-ink">{{ partner.name || 'Unnamed partner' }}</p>
                        <span class="mt-1 inline-flex rounded-full bg-[#fbf1e6] px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-brand">
                          {{ partner.categoryLabel || getPartnerCategoryLabel(partner.category) || 'Uncategorized' }}
                        </span>
                      </div>
                    </div>
                    <div class="mt-4 space-y-2 text-sm text-[#6f5a48]">
                      <p class="inline-flex items-center gap-2">
                        <i class="ri-phone-line text-brand"></i>
                        <span>{{ partner.displayPhone || (partner.phone ? `${partner.phoneCode} ${partner.phone}` : 'No phone added') }}</span>
                      </p>
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    <button
                      type="button"
                      class="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#e5d8cc] text-brand transition hover:border-brand hover:bg-[#fbf1e6]"
                      @click="editPartner(partner.id)"
                    >
                      <i class="ri-pencil-line"></i>
                    </button>
                    <button
                      type="button"
                      class="inline-flex h-9 w-9 items-center justify-center rounded-full border border-red-200 text-red-600 transition hover:border-red-300 hover:bg-red-50 disabled:opacity-60"
                      :disabled="deletingPartnerId === String(partner.id)"
                      @click="deletePartner(partner.id)"
                    >
                      <i v-if="deletingPartnerId !== String(partner.id)" class="ri-delete-bin-line"></i>
                      <i v-else class="ri-loader-4-line animate-spin"></i>
                    </button>
                  </div>
                </div>
              </article>
            </div>
          </template>

          <template v-else-if="activeSection === 'photos'">
            <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
              <h3 class="font-serif text-[2rem] font-semibold text-ink">Venue gallery</h3>
              <p class="mt-2 text-sm text-muted">Select a gallery category and add images in one uploader. They will upload automatically for that tab.</p>

              <div v-if="venueGalleryCategories.length" class="mt-5 flex flex-wrap gap-2">
                <button
                  v-for="category in venueGalleryCategories"
                  :key="category.id"
                  type="button"
                  class="rounded-full border px-4 py-2 text-sm font-medium transition"
                  :class="activeVenueGalleryTag === category.id ? 'border-brand bg-[#fbf1e6] text-brand' : 'border-[#e5d8cc] bg-white text-muted hover:border-brand hover:text-brand'"
                  @click="selectVenueGalleryCategory(category.id)"
                >
                  {{ category.label }}
                </button>
              </div>

              <div class="venue-gallery-pond mt-6 overflow-hidden rounded-[22px] border border-[#eadfd4] bg-[#fcf7f2] p-4">
                <FilePond
                  name="venueGallery"
                  allow-multiple
                  :disabled="!currentVenueId || !uploadableVenueGalleryTag || venueGalleryUploading"
                  accepted-file-types="image/jpeg, image/png, image/webp"
                  label-idle="Drag & drop venue gallery images or <span class='filepond--label-action'>Browse</span>"
                  :files="venueGalleryFiles"
                  @updatefiles="handleVenueGalleryFiles"
                />
                <p v-if="venueGalleryUploading" class="mt-2 text-sm font-medium text-brand">Uploading images...</p>
                <p v-if="activeVenueGalleryTag === allVenueGalleryTagId" class="mt-2 text-sm text-amber-700">Choose a specific tag to upload. `All` is view-only.</p>
              </div>

              <div v-if="venueGallery.length" class="mt-6 flex flex-wrap gap-3">
                <article
                  v-for="(image, index) in venueGallery"
                  :key="`${image.id}-${index}`"
                  class="group relative w-[128px] overflow-hidden rounded-2xl border border-[#eadfd4] bg-[#fcf7f2] shadow-[0_2px_8px_rgba(91,70,56,0.06)] transition hover:-translate-y-0.5 hover:border-[#d8b89a] hover:shadow-[0_12px_24px_rgba(91,70,56,0.12)]"
                >
                  <button
                    type="button"
                    class="block w-full text-left"
                    @click="openVenueGallery(index)"
                  >
                    <div class="relative h-[96px] overflow-hidden bg-[#f3ebe2]">
                      <img :src="image.src" :alt="`Venue gallery ${index + 1}`" class="h-full w-full object-cover transition duration-300 group-hover:scale-[1.04]">
                      <div class="absolute inset-0 bg-[linear-gradient(180deg,rgba(73,53,38,0.10)_0%,rgba(73,53,38,0.52)_100%)] opacity-0 transition duration-200 group-hover:opacity-100"></div>
                      <span v-if="image.isCover" class="absolute right-2 top-2 rounded-full bg-white/95 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#6b4d39] shadow-sm">Cover</span>
                      <div class="absolute inset-x-2 top-1/2 flex -translate-y-1/2 flex-col items-center gap-2 justify-center opacity-0 transition duration-200 group-hover:opacity-100">
                        <button
                          type="button"
                          class="rounded-full bg-white/95 px-3 py-1.5 text-[11px] font-semibold text-[#6b4d39] shadow-[0_8px_18px_rgba(91,70,56,0.15)] transition hover:bg-white disabled:opacity-60"
                          :disabled="saving || updatingVenueCoverId === String(image.id) || image.isCover"
                          @click.stop="setVenueGalleryCover(image)"
                        >
                          {{ image.isCover ? 'Cover image' : updatingVenueCoverId === String(image.id) ? 'Saving...' : 'Set as cover' }}
                        </button>
                        <button
                          type="button"
                          class="rounded-full bg-[#d4524a]/95 px-3 py-1.5 text-[11px] font-semibold text-white shadow-[0_8px_18px_rgba(121,36,29,0.25)] transition hover:bg-[#c04038]"
                          :disabled="saving || updatingVenueCoverId === String(image.id)"
                          @click.stop="deleteVenueGalleryImage(image.id)"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    <div class="border-t border-[#efe4d8] px-3 py-2">
                      <p class="truncate text-[11px] font-medium text-[#9a8b7c]">{{ image.tagName || activeVenueGalleryLabel }}</p>
                    </div>
                  </button>
                </article>
              </div>

              <div v-else class="mt-6 rounded-2xl border border-dashed border-[#eadfd4] bg-[#fcf7f2] px-4 py-6 text-sm text-muted">
                No venue gallery images uploaded yet.
              </div>
            </section>
          </template>

          <template v-else-if="activeSection === 'availability'">
            <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
              <div class="rounded-[20px] border border-[#eadfd4] bg-[#fffdfa] p-5">
                <h3 class="font-serif text-[1.95rem] font-semibold text-ink">Availability</h3>

                <div class="mt-5 grid gap-4 md:grid-cols-2">
                  <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                    Lead time (weeks)
                    <input v-model.trim="venueAvailabilityForm.leadTimeWeeks" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                    <span class="mt-2 block text-xs font-normal normal-case text-muted">Minimum notice required to book</span>
                  </label>

                  <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                    Max advance booking (weeks)
                    <input v-model.trim="venueAvailabilityForm.maxAdvanceBookingWeeks" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                    <span class="mt-2 block text-xs font-normal normal-case text-muted">How far ahead customers can book</span>
                  </label>
                </div>

                <div class="mt-8">
                  <p class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Operating hours - toggle off for closed days</p>

                  <div class="mt-4 divide-y divide-[#f1e7dc]">
                    <div
                      v-for="day in venueAvailabilityForm.operatingHours"
                      :key="day.key"
                      class="flex flex-wrap items-center gap-3 py-4"
                    >
                      <div class="w-10 text-lg font-semibold text-[#4f3a2c]">{{ day.label }}</div>

                      <button
                        type="button"
                        role="switch"
                        :aria-checked="day.enabled"
                        class="relative h-8 w-12 rounded-full transition"
                        :class="day.enabled ? 'bg-[#2f7a49]' : 'bg-[#d8c8b9]'"
                        @click="day.enabled = !day.enabled"
                      >
                        <span
                          class="absolute top-1 h-6 w-6 rounded-full bg-white shadow-sm transition"
                          :class="day.enabled ? 'left-5' : 'left-1'"
                        ></span>
                      </button>

                      <div class="flex flex-wrap items-center gap-2 sm:flex-nowrap">
                        <div class="relative">
                          <input
                            v-model="day.from"
                            type="time"
                            class="w-[128px] min-w-0 rounded-xl border border-[#e8ddd1] bg-white px-3 py-2.5 text-sm text-ink focus:border-brand focus:outline-none"
                            :disabled="!day.enabled"
                          >
                        </div>

                        <span class="text-[1.05rem] text-[#8d7766]">to</span>

                        <div class="relative">
                          <input
                            v-model="day.to"
                            type="time"
                            class="w-[128px] min-w-0 rounded-xl border border-[#e8ddd1] bg-white px-3 py-2.5 text-sm text-ink focus:border-brand focus:outline-none"
                            :disabled="!day.enabled"
                          >
                        </div>

                        <span
                          v-if="!day.enabled"
                          class="rounded-full bg-[#f2e6d9] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#8e6d52]"
                        >
                          Closed
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-6 flex justify-end">
                <button type="button" class="rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark disabled:opacity-60" :disabled="saving || !currentVenueId" @click="saveVenueAvailabilitySection">
                  {{ saving ? 'Saving...' : 'Save availability' }}
                </button>
              </div>
            </section>
          </template>

          <template v-else-if="activeSection === 'seo'">
            <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
              <div class="rounded-[20px] border border-[#eadfd4] bg-[#fffdfa] p-5">
                <h3 class="font-serif text-[1.95rem] font-semibold text-ink">SEO &amp; listing metadata</h3>

                <div v-if="!currentVenueId" class="mt-5 rounded-2xl border border-dashed border-[#eadfd4] bg-[#fcf7f2] px-4 py-5 text-sm text-muted">
                  Save the venue identity first before editing SEO metadata.
                </div>

                <div v-else class="mt-5 space-y-5">
                  <label class="block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                    URL slug
                    <input v-model.trim="venueSeoForm.slug" type="text" placeholder="venue-name" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                    <span class="mt-2 block text-xs font-normal normal-case text-muted">marketplace.com/{{ venueSeoPreviewSlug }}</span>
                  </label>

                  <label class="block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                    Meta description
                    <textarea v-model.trim="venueSeoForm.metaDescription" rows="4" placeholder="Write a concise description for search results." class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none"></textarea>
                    <span class="mt-2 block text-xs font-normal normal-case text-muted">~160 characters shown in Google search results</span>
                  </label>
                </div>
              </div>

              <div class="mt-6 flex justify-end">
                <button type="button" class="rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark disabled:opacity-60" :disabled="saving || !currentVenueId" @click="saveVenueSeoSection">
                  {{ saving ? 'Saving...' : 'Save SEO details' }}
                </button>
              </div>
            </section>
          </template>

          <template v-else-if="activeSection === 'preview'">
            <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
              <div class="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b5947b]">
                    <i class="ri-eye-line mr-1"></i>
                    Listing Preview
                  </p>
                  <h3 class="mt-2 font-serif text-[2rem] font-semibold text-ink">Marketplace card + detail page launch</h3>
                  <p class="mt-2 max-w-2xl text-sm leading-6 text-muted">
                    Review the compact listing card here, then open the full venue detail preview page to inspect the customer-facing layout.
                  </p>
                </div>

                <button
                  type="button"
                  class="rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark disabled:opacity-60"
                  :disabled="!currentVenueId"
                  @click="openListingPreviewPage"
                >
                  Open venue detail preview
                </button>
              </div>

              <div class="mt-6">
                <div class="mx-auto max-w-[360px] overflow-hidden rounded-[24px] border border-[#e6d8ca] bg-[#fdf8f2] shadow-[0_18px_45px_rgba(117,88,60,0.10)]">
                  <div class="relative h-[220px] overflow-hidden bg-[linear-gradient(135deg,#f5e8da_0%,#f9f1e8_54%,#efe1d1_100%)]">
                    <span class="absolute left-4 top-4 rounded-full bg-[#6f6760] px-3 py-1 text-[11px] font-semibold text-white shadow-sm">
                      {{ listingPreviewBadge }}
                    </span>
                    <span class="absolute right-4 top-4 rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold text-[#7d634e] shadow-sm">
                      Preview card
                    </span>
                    <img
                      v-if="listingPreviewImage"
                      :src="listingPreviewImage"
                      :alt="listingPreviewTitle"
                      class="h-full w-full object-cover"
                    >
                    <div v-else class="flex h-full items-center justify-center text-[#9da8b2]">
                      <i class="ri-building-4-line text-[4.5rem]"></i>
                    </div>
                  </div>

                  <div class="bg-white px-5 py-5">
                    <div class="flex flex-wrap gap-2">
                      <span v-for="item in listingPreviewHighlights" :key="item" class="rounded-full bg-[#fff4ea] px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#b8612b]">
                        {{ item }}
                      </span>
                    </div>

                    <p class="mt-4 font-serif text-[1.8rem] font-semibold leading-tight text-[#1f1208]">{{ listingPreviewTitle }}</p>
                    <p class="mt-2 text-[1.02rem] text-[#9c7a60]">
                      <span class="font-semibold text-[#cf682d]">by {{ listingPreviewBusiness }}</span>
                      <span class="mx-1 text-[#ccb8a4]">·</span>
                      <span>{{ listingPreviewCity }}</span>
                    </p>

                    <div class="mt-4 flex flex-wrap gap-2 text-xs text-[#7d634e]">
                      <span v-for="item in listingPreviewStats" :key="item" class="rounded-full border border-[#eadfd4] bg-[#fffaf5] px-3 py-1 font-semibold">
                        {{ item }}
                      </span>
                    </div>

                    <div class="mt-5 flex items-center justify-between gap-4">
                      <p class="text-[1.15rem] font-semibold text-[#9e5b33]">{{ listingPreviewPrice }}</p>
                      <button
                        type="button"
                        class="rounded-xl bg-[#bf672f] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#a95624] disabled:opacity-60"
                        :disabled="!currentVenueId"
                        @click="openListingPreviewPage"
                      >
                        View details
                      </button>
                    </div>
                  </div>

                  <div class="border-t border-[#eee2d6] bg-[#fcf7f1] px-5 py-3 text-sm text-[#b18d72]">
                    marketplace.com/<span class="text-[#a35d30]">{{ listingPreviewSlug }}</span>
                  </div>
                </div>
              </div>
            </section>
          </template>

          <div v-else class="rounded-[24px] border border-dashed border-[#eadfd4] bg-white p-8 text-sm text-muted">
            {{ sectionLabel }} is not wired yet in this build.
          </div>
        </div>

        <div v-else class="space-y-6">
          <template v-if="activeSection === 'brand'">
            <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
              <h3 class="font-serif text-[2rem] font-semibold text-ink">Category</h3>
              <div class="mt-5 grid gap-3 md:grid-cols-2">
                <button
                  v-for="category in vendorCategories"
                  :key="category.name"
                  type="button"
                  class="flex items-center gap-3 rounded-2xl border px-4 py-4 text-left transition"
                  :class="vendorForm.category === category.name ? 'border-brand bg-[#fbf1e6]' : 'border-[#eadfd4] bg-white hover:border-brand'"
                  @click="vendorForm.category = category.name"
                >
                  <span class="text-2xl">{{ category.icon }}</span>
                  <span>
                    <span class="block text-sm font-semibold text-ink">{{ category.name }}</span>
                    <span class="block text-xs text-muted">{{ category.meta }}</span>
                  </span>
                </button>
              </div>
            </section>

            <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
              <h3 class="font-serif text-[2rem] font-semibold text-ink">Brand identity</h3>
              <div class="mt-5 grid gap-4 md:grid-cols-2">
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638] md:col-span-2">
                  Brand name (public) <span class="text-brand">*</span>
                  <input v-model.trim="vendorForm.brandName" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                </label>
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638] md:col-span-2">
                  Tagline
                  <input v-model.trim="vendorForm.tagline" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                </label>
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638] md:col-span-2">
                  About
                  <textarea v-model.trim="vendorForm.about" rows="4" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none"></textarea>
                </label>
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  City
                  <input v-model.trim="vendorForm.city" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                </label>
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  State
                  <input v-model.trim="vendorForm.state" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                </label>
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  Phone
                  <input v-model.trim="vendorForm.phone" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                </label>
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  Email
                  <input v-model.trim="vendorForm.email" type="email" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                </label>
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  Website
                  <input v-model.trim="vendorForm.website" type="text" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                </label>
              </div>
              <div class="mt-6">
                <p class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Highlights</p>
                <div class="mt-3 flex flex-wrap gap-2">
                  <button
                    v-for="tag in sharedHighlightOptions"
                    :key="tag"
                    type="button"
                    class="rounded-full border px-3 py-1.5 text-xs font-medium transition"
                    :class="vendorForm.highlights.includes(tag) ? 'border-brand bg-[#fbf1e6] text-brand' : 'border-[#e5d8cc] bg-white text-muted hover:border-brand hover:text-brand'"
                    @click="toggleTag(vendorForm.highlights, tag)"
                  >
                    {{ tag }}
                  </button>
                </div>
              </div>
            </section>
          </template>

          <template v-else-if="activeSection === 'catalog'">
            <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
              <h3 class="font-serif text-[2rem] font-semibold text-ink">Service catalog</h3>
              <p class="mt-2 text-sm text-muted">Specific details customers use to filter and compare vendors.</p>

              <div class="mt-6">
                <p class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Themes</p>
                <div class="mt-3 flex flex-wrap gap-2">
                  <button
                    v-for="theme in serviceCatalogThemeOptions"
                    :key="theme"
                    type="button"
                    class="rounded-full border px-4 py-2 text-sm transition"
                    :class="vendorForm.catalogThemes.includes(theme) ? 'border-brand bg-[#fbf1e6] text-brand' : 'border-[#e5d8cc] bg-white text-muted hover:border-brand hover:text-brand'"
                    @click="toggleTag(vendorForm.catalogThemes, theme)"
                  >
                    {{ theme }}
                  </button>
                </div>
              </div>

              <div class="mt-6">
                <p class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Styles</p>
                <div class="mt-3 flex flex-wrap gap-2">
                  <button
                    v-for="style in serviceCatalogStyleOptions"
                    :key="style"
                    type="button"
                    class="rounded-full border px-4 py-2 text-sm transition"
                    :class="vendorForm.catalogStyles.includes(style) ? 'border-brand bg-[#fbf1e6] text-brand' : 'border-[#e5d8cc] bg-white text-muted hover:border-brand hover:text-brand'"
                    @click="toggleTag(vendorForm.catalogStyles, style)"
                  >
                    {{ style }}
                  </button>
                </div>
              </div>

              <div class="mt-6 grid gap-5 md:grid-cols-2">
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  Min budget (₹)
                  <input
                    v-model.trim="vendorForm.minBudget"
                    type="text"
                    inputmode="numeric"
                    placeholder="e.g. 50000"
                    class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none"
                  >
                </label>
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  Decoration starting price (₹)
                  <input
                    v-model.trim="vendorForm.startingPrice"
                    type="text"
                    inputmode="numeric"
                    placeholder="e.g. 150000"
                    class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none"
                  >
                </label>
              </div>
            </section>
          </template>

          <template v-else-if="activeSection === 'policies'">
            <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
              <h3 class="font-serif text-[2rem] font-semibold text-ink">Cancellation policy</h3>
              <p class="mt-2 text-sm text-muted">Two separate policies - when the customer cancels, and when you cancel.</p>

              <div class="mt-6 grid gap-5 md:grid-cols-2">
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  If customer cancels <span class="text-brand">*</span>
                  <select v-model="vendorForm.customerCancellationPolicy" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                    <option v-for="option in vendorCancellationPolicyOptions" :key="`customer-${option}`" :value="option">{{ option }}</option>
                  </select>
                </label>

                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  If you (vendor) cancel <span class="text-brand">*</span>
                  <select v-model="vendorForm.vendorCancellationPolicy" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                    <option v-for="option in vendorCancellationPolicyOptions" :key="`vendor-${option}`" :value="option">{{ option }}</option>
                  </select>
                </label>
              </div>

              <div class="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-[22px] border border-[#eadfd4] bg-[#fcf7f2] px-5 py-4">
                <div>
                  <p class="text-[1.02rem] font-medium text-ink">Date adjustment offered - no refund but event date can be rescheduled</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  :aria-checked="vendorForm.dateAdjustmentOffered"
                  class="relative h-8 w-12 rounded-full transition"
                  :class="vendorForm.dateAdjustmentOffered ? 'bg-[#2f7a49]' : 'bg-[#d8c8b9]'"
                  @click="vendorForm.dateAdjustmentOffered = !vendorForm.dateAdjustmentOffered"
                >
                  <span class="absolute top-1 h-6 w-6 rounded-full bg-white shadow-sm transition" :class="vendorForm.dateAdjustmentOffered ? 'left-5' : 'left-1'"></span>
                </button>
              </div>

              <div class="mt-6">
                <p class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">Tiered cancellation (days before event → refund %)</p>
                <div class="mt-4 space-y-4">
                  <div
                    v-for="(tier, index) in vendorForm.cancellationTiers"
                    :key="`vendor-tier-${index}`"
                    class="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_28px]"
                  >
                    <div>
                      <input
                        v-model.trim="tier.days"
                        type="text"
                        inputmode="numeric"
                        placeholder="e.g. 30"
                        class="w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none"
                        @input="syncVendorCancellationTier(index, 'days')"
                      >
                      <p v-if="index === 0" class="mt-2 text-xs normal-case text-muted">e.g. 60 = 60 days before</p>
                    </div>

                    <div>
                      <input
                        v-model.trim="tier.refund"
                        type="text"
                        inputmode="numeric"
                        placeholder="e.g. 100"
                        class="w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none"
                        @input="syncVendorCancellationTier(index, 'refund')"
                      >
                      <p v-if="index === 0" class="mt-2 text-xs normal-case text-muted">e.g. 100 = full refund</p>
                    </div>

                    <button
                      type="button"
                      class="mt-3 inline-flex h-10 w-7 items-center justify-center text-base text-[#d4524a] transition hover:text-[#bb3f37] disabled:cursor-not-allowed disabled:opacity-30 md:mt-0"
                      :disabled="vendorForm.cancellationTiers.length <= 1"
                      @click="removeVendorCancellationTier(index)"
                    >
                      ×
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  class="mt-5 rounded-xl border border-[#e5d8cc] bg-white px-4 py-2.5 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand"
                  @click="addVendorCancellationTier"
                >
                  + Add tier
                </button>
              </div>

              <label class="mt-6 block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Cancellation T&amp;C (full detail)
                <textarea
                  v-model.trim="vendorForm.cancellationTerms"
                  rows="4"
                  class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none"
                ></textarea>
                <span class="mt-2 block text-xs font-normal normal-case text-muted">Write out the full terms - this text appears on the listing</span>
              </label>

              <label class="mt-6 block text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                Deposit % to block a date
                <input
                  v-model.trim="vendorForm.depositPercent"
                  type="text"
                  inputmode="numeric"
                  placeholder="e.g. 25"
                  class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none"
                  @input="syncVendorPolicyField('depositPercent', 3)"
                >
                <span class="mt-2 block text-xs font-normal normal-case text-muted">% of total collected upfront to confirm the booking</span>
              </label>

              <div class="mt-6 flex flex-wrap justify-end gap-3">
                <button
                  type="button"
                  class="rounded-xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="saving"
                  @click="saveVendorPoliciesSection(true)"
                >
                  {{ saving ? 'Saving...' : 'Save & exit' }}
                </button>
                <button
                  type="button"
                  class="rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="saving"
                  @click="saveVendorPoliciesSection(false)"
                >
                  {{ saving ? 'Saving...' : 'Save policies' }}
                </button>
              </div>
            </section>
          </template>

          <template v-else-if="activeSection === 'pricing'">
            <section class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
              <h3 class="font-serif text-[2rem] font-semibold text-ink">Pricing mode</h3>
              <p class="mt-2 text-sm text-muted">Choose how your pricing appears to customers on the marketplace.</p>

              <div class="mt-5 grid gap-3 lg:grid-cols-3">
                <button
                  v-for="option in vendorPricingModeOptions"
                  :key="option.id"
                  type="button"
                  class="rounded-[22px] border px-5 py-5 text-left transition"
                  :class="vendorForm.pricingMode === option.id ? 'border-brand bg-[#fff2ea]' : 'border-[#eadfd4] bg-white hover:border-brand'"
                  @click="vendorForm.pricingMode = option.id"
                >
                  <i :class="[option.icon, 'text-[1.6rem] text-[#b78969]']"></i>
                  <p class="mt-4 text-[1.35rem] font-semibold text-ink">{{ option.label }}</p>
                  <p class="mt-2 text-sm leading-6 text-muted">{{ option.meta }}</p>
                </button>
              </div>

              <div class="mt-4 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-4 text-sm text-sky-800">
                {{ vendorPricingModeOptions.find((item) => item.id === vendorForm.pricingMode)?.helper || '' }}
              </div>
            </section>

            <section v-if="vendorForm.pricingMode === 'packages'" class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
              <h3 class="font-serif text-[1.95rem] font-semibold text-ink">Packages</h3>
              <div class="mt-5">
                <button
                  type="button"
                  class="rounded-2xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand"
                  :disabled="!moduleId"
                  @click="openPackageCreatePage"
                >
                  + Add package
                </button>
              </div>

              <div v-if="!moduleId" class="mt-5 rounded-2xl border border-dashed border-[#eadfd4] bg-[#fcf7f2] p-5 text-sm text-muted">
                Save the vendor identity first before adding packages.
              </div>

              <div v-else-if="vendorForm.packages.length" class="mt-5 grid gap-4 md:grid-cols-2">
                <article v-for="pkg in vendorForm.packages" :key="pkg.id" class="rounded-[22px] border border-[#eadfd4] bg-white p-5">
                  <div class="flex items-start justify-between gap-4">
                    <div>
                      <p class="text-[1.35rem] font-semibold text-ink">{{ pkg.name }}</p>
                      <div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted">
                        <span>₹{{ pkg.price || '0' }}</span>
                        <span>{{ pkg.maxGuests || 'N/A' }} guests</span>
                        <span>{{ pkg.validHours || 'N/A' }}</span>
                      </div>
                    </div>
                    <div class="flex items-center gap-2">
                      <button
                        type="button"
                        class="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#e5d8cc] text-brand transition hover:border-brand hover:bg-[#fbf1e6]"
                        @click="editPackage(pkg.id)"
                      >
                        <i class="ri-pencil-line"></i>
                      </button>
                      <button
                        type="button"
                        class="inline-flex h-9 w-9 items-center justify-center rounded-full border border-red-200 text-red-600 transition hover:border-red-300 hover:bg-red-50 disabled:opacity-60"
                        :disabled="deletingPackageId === String(pkg.id)"
                        @click="deletePackage(pkg.id)"
                      >
                        <i v-if="deletingPackageId !== String(pkg.id)" class="ri-delete-bin-line"></i>
                        <i v-else class="ri-loader-4-line animate-spin"></i>
                      </button>
                    </div>
                  </div>
                  <p class="mt-4 text-sm text-muted">{{ pkg.description || 'No package summary added.' }}</p>
                  <div v-if="pkg.includedServices.length" class="mt-3 flex flex-wrap gap-2">
                    <span
                      v-for="service in visiblePackageServices(pkg)"
                      :key="`${pkg.id}-${service}`"
                      class="inline-flex rounded-full bg-[#fbf1e6] px-3 py-1 text-xs font-medium text-brand"
                    >
                      {{ service }}
                    </span>
                  </div>
                </article>
              </div>
            </section>

            <section v-else-if="vendorForm.pricingMode === 'quote-range'" class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
              <h3 class="font-serif text-[1.95rem] font-semibold text-ink">Price range</h3>
              <p class="mt-2 text-sm text-muted">Customers see this range and contact you for a custom quote.</p>

              <div class="mt-5">
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  Pricing unit
                  <select v-model="vendorForm.pricingUnit" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none">
                    <option v-for="item in vendorPricingUnitOptions" :key="item" :value="item">{{ item }}</option>
                  </select>
                </label>
              </div>

              <div class="mt-5 grid gap-4 md:grid-cols-2">
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  Starting from (₹)
                  <input v-model.trim="vendorForm.priceRangeFrom" type="text" inputmode="numeric" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none" @input="syncVendorPolicyField('priceRangeFrom', 8)">
                </label>
                <label class="text-xs font-semibold uppercase tracking-wide text-[#5b4638]">
                  Up to (₹)
                  <input v-model.trim="vendorForm.priceRangeTo" type="text" inputmode="numeric" class="mt-2 w-full rounded-2xl border border-[#e8ddd1] px-4 py-3 text-sm normal-case font-normal focus:border-brand focus:outline-none" @input="syncVendorPolicyField('priceRangeTo', 8)">
                </label>
              </div>
            </section>

            <section v-else class="rounded-[24px] border border-[#eadfd4] bg-white p-6">
              <h3 class="font-serif text-[1.95rem] font-semibold text-ink">Per-space pricing</h3>
              <p class="mt-2 text-sm text-muted">Prices are set inside each space in the Spaces section.</p>
              <div class="mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-800">
                Go to Spaces and set min/max price on each space card.
              </div>
            </section>

            <div class="flex justify-end">
              <button
                type="button"
                class="rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="saving"
                @click="saveVendorPricingSection"
              >
                {{ saving ? 'Saving...' : 'Save pricing' }}
              </button>
            </div>
          </template>

          <div v-else class="rounded-[24px] border border-dashed border-[#eadfd4] bg-white p-8 text-sm text-muted">
            {{ sectionLabel }} is not wired yet in this build.
          </div>
        </div>

        <p v-if="error" class="mt-6 text-sm font-semibold text-red-600">{{ error }}</p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import vueFilePond from 'vue-filepond'
import 'filepond/dist/filepond.min.css'
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import lightGallery from 'lightgallery'
import lgThumbnail from 'lightgallery/plugins/thumbnail'
import lgZoom from 'lightgallery/plugins/zoom'
import markerIcon from '@/assets/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import Swal from 'sweetalert2'
import api from '@/services/vendor/api'
import { getAuthUserId } from '@/services/auth'
import countries from '@/data/countries.json'

const route = useRoute()
const router = useRouter()
const defaultCountryPhoneCode = '+91'

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon,
  iconUrl: markerIcon,
  shadowUrl: markerShadow
})

const FilePond = vueFilePond(FilePondPluginImagePreview, FilePondPluginFileValidateType)

const saving = ref(false)
const error = ref('')
const activeSection = ref('identity')

const showSuccessToast = async (title) => {
  await Swal.fire({
    toast: true,
    position: 'top-end',
    icon: 'success',
    title,
    showConfirmButton: false,
    timer: 1800,
    timerProgressBar: true
  })
}

const showErrorToast = async (title) => {
  await Swal.fire({
    toast: true,
    position: 'top-end',
    icon: 'error',
    title,
    showConfirmButton: false,
    timer: 2400,
    timerProgressBar: true
  })
}

const venueSections = [
  { id: 'overview', label: 'Overview' },
  { id: 'identity', label: 'Venue identity' },
  { id: 'details', label: 'Venue details' },
  { id: 'spaces', label: 'Spaces' },
  { id: 'packages', label: 'Packages' },
  { id: 'policies', label: 'Policies' },
  { id: 'partners', label: 'Vendor partners' },
  { id: 'photos', label: 'Photo library' },
  { id: 'seo', label: 'Seo & Listing' },
  { id: 'availability', label: 'Availability' },
  { id: 'preview', label: 'Listing Preview' }
]

const vendorSections = [
  { id: 'overview', label: 'Overview' },
  { id: 'brand', label: 'Brand identity' },
  { id: 'catalog', label: 'Service catalog' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'policies', label: 'Policies' },
  { id: 'photos', label: 'Photos' },
  { id: 'availability', label: 'Availability' },
  { id: 'seo', label: 'SEO & listing' },
  { id: 'preview', label: 'Listing preview' }
]

const sharedHighlightOptions = [
  'Award Winning',
  '5-Star Rated',
  'Eco-Friendly',
  'Pet Friendly',
  'Luxury',
  'Budget Friendly',
  'Heritage Property',
  'Beachfront',
  'Waterfront',
  'Mountain View',
  'Lake / River View',
  'Sea View',
  'Rooftop',
  'Garden Setting',
  'Veg Only Venue',
  'LGBTQ+ Friendly',
  'Child Friendly'
]

const cateringInHouseOptions = [
  { value: 'Yes - we have our own kitchen / catering team', label: 'Yes - we have our own kitchen / catering team', icon: '✅' },
  { value: 'No - we do not serve food in-house', label: 'No - we do not serve food in-house', icon: '❌' }
]

const cateringOutsideOptions = [
  { value: 'Not allowed - only in-house or no food', label: 'Not allowed - only in-house or no food', icon: '🚫' },
  { value: 'Allowed freely - any caterer welcome', label: 'Allowed freely - any caterer welcome', icon: '✅' },
  { value: 'Partners / panel only - from approved list', label: 'Partners / panel only - from approved list', icon: '📋' }
]

const decorPolicyOptions = [
  'In-house decor only',
  'Enlisted panel decorators only',
  'Outside decorators permitted',
  'In-house + outside allowed'
]

const musicPolicyOptions = [
  'In-house DJ + outside permitted',
  'In-house DJ only - outside not permitted',
  'No in-house DJ - outside permitted',
  'No DJ services - outside not permitted either'
]

const venueHighlightOptions = ref([])
const venueTypeOptions = ref([])
const venueEventsHostedOptions = ref([])
const venueAmenitiesOptions = ref([])
const venueInHouseServiceOptions = ref([])
const venueSpaceTypeOptions = ref([])
const venueSpaceSeatConfigOptions = ref([])
const partnerCategoryOptions = ref([])
const partnerCategoryDropdownOpen = ref(false)
const partnerCategorySearchQuery = ref('')
const publishingVenue = ref(false)

const spaceSettingOptions = ['Indoor', 'Outdoor', 'Semi-outdoor']

const vendorCategories = [
  { name: 'Catering', meta: 'Food and beverage services', icon: '🍽️' },
  { name: 'Decoration', meta: 'Decoration and styling', icon: '🌸' },
  { name: 'Photography', meta: 'Photo and video', icon: '📸' },
  { name: 'Entertainment', meta: 'DJ, bands, performers', icon: '🎵' },
  { name: 'Planning', meta: 'Event management', icon: '📋' },
  { name: 'Rentals', meta: 'Furniture, equipment', icon: '🪑' },
  { name: 'Makeup & Grooming', meta: 'Bridal, grooming', icon: '💄' }
]

const serviceCatalogThemeOptions = [
  'Traditional',
  'Modern',
  'Rustic',
  'Royal',
  'Bohemian',
  'Minimalist',
  'Floral Fantasy',
  'Fairy Tale'
]

const serviceCatalogStyleOptions = [
  'Floral',
  'Balloon',
  'Fabric Draping',
  'Lighting',
  'Mixed Media'
]

const vendorCancellationPolicyOptions = [
  'Full Refund',
  'Partial Refund',
  'Non Refundable',
  'Custom Terms'
]

const vendorPricingModeOptions = [
  {
    id: 'packages',
    label: 'Packages',
    meta: 'Fixed bundles - customers choose a package',
    icon: 'ri-box-3-line',
    helper: 'Marketplace shows package cards. Customers select a package to inquire.'
  },
  {
    id: 'quote-range',
    label: 'Quote / Range',
    meta: 'Price range shown - customer sends inquiry',
    icon: 'ri-message-3-line',
    helper: "Marketplace shows 'Starting from ₹X' with a Request Quote button. No direct booking."
  },
  {
    id: 'per-space',
    label: 'Per Space',
    meta: 'Each space priced individually',
    icon: 'ri-government-line',
    helper: 'Each space card shows its own price range. Set min/max prices inside each space.'
  }
]

const vendorPricingUnitOptions = ['Per Event', 'Per Day', 'Per Hour', 'Per Session']

const vendorPackageTagOptions = [
  'Venue',
  'Catering',
  'Decoration',
  'DJ',
  'Photography',
  'Accommodation',
  'Basic Setup',
  'Premium',
  'Travel',
  'Equipment',
  'Album',
  'Reel'
]

const venueForm = reactive({
  id: '',
  title: '',
  type: '',
  tagLine: '',
  about: '',
  address: '',
  latitude: '',
  longitude: '',
  phoneCode: defaultCountryPhoneCode,
  phone: '',
  email: '',
  website: '',
  instagram: '',
  facebook: '',
  youtube: '',
  highlights: []
})

const locationPickerOpen = ref(false)
const locationSearchQuery = ref('')
const locationSearchResults = ref([])
const locationSearchLoading = ref(false)
const locationCurrentLoading = ref(false)
const locationPickerError = ref('')
const locationPickerAddress = ref('')
const locationPickerLat = ref('')
const locationPickerLng = ref('')
const locationMap = ref(null)
const locationMarker = ref(null)

const venueDetailsForm = reactive({
  venueType: '',
  yearEstablished: '',
  eventsHosted: [],
  amenities: [],
  inHouseServices: []
})

const venuePackages = ref([])
const expandedPackageServices = ref({})
const packageFormVisible = ref(false)
const editingPackageId = ref('')
const deletingPackageId = ref('')
const packageForm = reactive({
  name: '',
  price: '',
  description: '',
  includedServices: [],
  maxGuests: '',
  validHours: ''
})

const venuePoliciesForm = reactive({
  inHouseCatering: '',
  outsideCatering: '',
  outsideCateringFeeType: 'No fee',
  outsideCateringFeeValue: '',
  decorPolicy: '',
  musicPolicy: '',
  alcoholInhouse: '0',
  alcoholOutside: '0',
  minGuests: '',
  maxGuests: '',
  decorTime: '',
  cleanTime: '',
  noiseMusic: '',
  otherHouseRules: '',
  cancelTerms: '',
  depositPercent: ''
})

const venueSeoForm = reactive({
  slug: '',
  metaDescription: ''
})

const venueAvailabilityForm = reactive({
  leadTimeWeeks: '',
  maxAdvanceBookingWeeks: '',
  operatingHours: [
    { key: 'mon', label: 'Mon', enabled: true, from: '09:00', to: '23:00', apiPrefix: 'mon' },
    { key: 'tue', label: 'Tue', enabled: true, from: '09:00', to: '23:00', apiPrefix: 'tues' },
    { key: 'wed', label: 'Wed', enabled: true, from: '09:00', to: '23:00', apiPrefix: 'wed' },
    { key: 'thu', label: 'Thu', enabled: true, from: '09:00', to: '23:00', apiPrefix: 'thurs' },
    { key: 'fri', label: 'Fri', enabled: true, from: '09:00', to: '23:00', apiPrefix: 'frid' },
    { key: 'sat', label: 'Sat', enabled: true, from: '09:00', to: '23:00', apiPrefix: 'sat' },
    { key: 'sun', label: 'Sun', enabled: true, from: '09:00', to: '23:00', apiPrefix: 'sun' }
  ]
})

const venuePartners = ref([])
const partnerFormVisible = ref(false)
const editingPartnerId = ref('')
const deletingPartnerId = ref('')
const partnerForm = reactive({
  name: '',
  category: '',
  phoneCode: defaultCountryPhoneCode,
  phone: ''
})

const venueGallery = ref([])
const venueGalleryFiles = ref([])
const venueGalleryCategories = ref([])
const activeVenueGalleryTag = ref('')
const venueGalleryUploading = ref(false)
const venueGalleryAutoUploadTimer = ref(null)
const updatingVenueCoverId = ref('')

const venueSpaces = ref([])
const spaceFormVisible = ref(false)
const editingSpaceId = ref('')
const deletingSpaceId = ref('')
const spaceForm = reactive({
  name: '',
  spaceType: '',
  setting: 'Indoor',
  areaSqft: '',
  seatingConfigurations: [],
  seatingCapacities: {},
  minPrice: '',
  maxPrice: '',
  amenities: [],
  description: ''
})

const vendorForm = reactive({
  id: '',
  category: 'Catering',
  brandName: '',
  tagline: '',
  about: '',
  city: '',
  state: '',
  phone: '',
  email: '',
  website: '',
  highlights: [],
  catalogThemes: [],
  catalogStyles: [],
  minBudget: '',
  startingPrice: '',
  customerCancellationPolicy: 'Partial Refund',
  vendorCancellationPolicy: 'Full Refund',
  dateAdjustmentOffered: false,
  cancellationTiers: [
    { days: '30', refund: '100' },
    { days: '15', refund: '50' },
    { days: '7', refund: '0' }
  ],
  cancellationTerms: '',
  depositPercent: '25',
  pricingMode: 'packages',
  pricingUnit: 'Per Event',
  priceRangeFrom: '',
  priceRangeTo: '',
  packages: []
})

const venueFieldErrors = reactive({
  title: '',
  type: '',
  tagLine: '',
  address: '',
  latitude: '',
  longitude: '',
  phone: '',
  email: '',
  website: ''
})

const venueDetailsErrors = reactive({
  venueType: '',
  yearEstablished: '',
  eventsHosted: ''
})

const packageFieldErrors = reactive({
  name: '',
  price: '',
  maxGuests: '',
  validHours: '',
  description: '',
  includedServices: ''
})

const spaceFieldErrors = reactive({
  name: '',
  spaceType: '',
  areaSqft: '',
  seatingConfigurations: '',
  minPrice: '',
  maxPrice: '',
  description: ''
})

const partnerFieldErrors = reactive({
  name: '',
  category: '',
  phone: ''
})

const businessId = computed(() => String(route.params.businessId || ''))
const moduleId = computed(() => String(route.params.moduleId || ''))
const currentVenueId = computed(() => String(venueForm.id || moduleId.value || ''))
const moduleType = computed(() => (String(route.params.moduleType || '') === 'vendor' ? 'vendor' : 'venue'))
const isEditMode = computed(() => Boolean(moduleId.value))
const moduleBadge = computed(() => (moduleType.value === 'venue' ? 'Venue' : 'Service'))
const sections = computed(() => (moduleType.value === 'venue' ? venueSections : vendorSections))
const requestedSection = computed(() => {
  const candidate = String(route.query.section || '').trim()
  return sections.value.some((item) => item.id === candidate) ? candidate : ''
})
const requestedPartnerMode = computed(() => String(route.query.partnerMode || '').trim().toLowerCase())
const requestedPartnerId = computed(() => String(route.query.partnerId || '').trim())
const isEditingPartnerRoute = computed(() => requestedPartnerMode.value === 'edit' && Boolean(requestedPartnerId.value))
const isCreatingPartnerRoute = computed(() => requestedPartnerMode.value === 'new')
const isPartnerEditorRoute = computed(() => moduleType.value === 'venue' && requestedSection.value === 'partners' && (isCreatingPartnerRoute.value || isEditingPartnerRoute.value))
const requestedPackageMode = computed(() => String(route.query.packageMode || '').trim().toLowerCase())
const requestedPackageId = computed(() => String(route.query.packageId || '').trim())
const isEditingPackageRoute = computed(() => requestedPackageMode.value === 'edit' && Boolean(requestedPackageId.value))
const isCreatingPackageRoute = computed(() => requestedPackageMode.value === 'new')
const isVenuePackageEditorRoute = computed(() => moduleType.value === 'venue' && requestedSection.value === 'packages' && (isCreatingPackageRoute.value || isEditingPackageRoute.value))
const isVendorPackageEditorRoute = computed(() => moduleType.value === 'vendor' && requestedSection.value === 'pricing' && (isCreatingPackageRoute.value || isEditingPackageRoute.value))
const isPackageEditorRoute = computed(() => isVenuePackageEditorRoute.value || isVendorPackageEditorRoute.value)
const requestedSpaceMode = computed(() => String(route.query.spaceMode || '').trim().toLowerCase())
const requestedSpaceId = computed(() => String(route.query.spaceId || '').trim())
const isEditingSpaceRoute = computed(() => requestedSpaceMode.value === 'edit' && Boolean(requestedSpaceId.value))
const isCreatingSpaceRoute = computed(() => requestedSpaceMode.value === 'new')
const isSpaceEditorRoute = computed(() => moduleType.value === 'venue' && requestedSection.value === 'spaces' && (isCreatingSpaceRoute.value || isEditingSpaceRoute.value))
const sectionLabel = computed(() => sections.value.find((item) => item.id === activeSection.value)?.label || 'This section')
const storageKey = computed(() => `business_${businessId.value}_${moduleType.value}_modules`)
const businessName = computed(() => String(route.query.businessName || 'Business module'))
const pageTitle = computed(() => {
  if (moduleType.value === 'venue') return isEditMode.value ? (venueForm.title || 'Edit venue') : 'New venue'
  return isEditMode.value ? (vendorForm.brandName || 'Edit service vendor') : 'New service vendor'
})
const venueOverviewChecklist = computed(() => [
  { label: 'Venue name', complete: Boolean(venueForm.title.trim()) },
  { label: 'Tagline', complete: Boolean(venueForm.tagLine.trim()) },
  { label: 'Address', complete: Boolean(venueForm.address.trim()) },
  { label: 'Spaces added', complete: venueSpaces.value.length > 0 },
  { label: 'Packages added', complete: venuePackages.value.length > 0 },
  { label: 'Policies set', complete: Boolean(venuePoliciesForm.inHouseCatering || venuePoliciesForm.outsideCatering || venuePoliciesForm.depositPercent) },
  { label: 'Gallery uploaded', complete: venueGallery.value.length > 0 },
  { label: 'Vendor partners', complete: venuePartners.value.length > 0 }
])
const venueCompletion = computed(() => {
  const total = venueOverviewChecklist.value.length || 1
  const completed = venueOverviewChecklist.value.filter((item) => item.complete).length
  return Math.round((completed / total) * 100)
})
const canPublishVenue = computed(() => moduleType.value === 'venue' && Boolean(currentVenueId.value) && venueCompletion.value >= 100)
const allVenueGalleryTagId = 'all'
const uploadableVenueGalleryTag = computed(() =>
  activeVenueGalleryTag.value && activeVenueGalleryTag.value !== allVenueGalleryTagId
    ? activeVenueGalleryTag.value
    : ''
)
const activeVenueGalleryLabel = computed(() => {
  const activeCategory = venueGalleryCategories.value.find((item) => item.id === activeVenueGalleryTag.value)
  if (!activeCategory || activeCategory.id === allVenueGalleryTagId) return 'Venue & Spaces'
  return activeCategory.label || 'Venue & Spaces'
})
const listingPreviewImage = computed(() => venueGallery.value[0]?.src || '')
const listingPreviewBadge = computed(() => getVenueTypeLabel(venueForm.type || venueDetailsForm.venueType) || 'Venue')
const listingPreviewTitle = computed(() => venueForm.title || 'Venue / Brand name')
const listingPreviewBusiness = computed(() => businessName.value || 'Business')
const listingPreviewHighlights = computed(() => {
  const items = [
    getVenueTypeLabel(venueDetailsForm.venueType),
    venueForm.highlights[0],
    venueSpaces.value.length ? `${venueSpaces.value.length} spaces` : '',
    venuePackages.value.length ? `${venuePackages.value.length} packages` : ''
  ]
  return items.map((item) => String(item || '').trim()).filter(Boolean).slice(0, 4)
})
const listingPreviewCity = computed(() => {
  const address = String(venueForm.address || '').trim()
  if (!address) return 'City'
  const segments = address.split(',').map((item) => item.trim()).filter(Boolean)
  if (!segments.length) return 'City'
  return segments.length >= 2 ? segments[segments.length - 2] : segments[0]
})
const listingPreviewStats = computed(() => {
  const items = []
  if (venueSpaces.value.length) items.push(`${venueSpaces.value.length} space${venueSpaces.value.length === 1 ? '' : 's'}`)
  if (venuePackages.value.length) items.push(`${venuePackages.value.length} package${venuePackages.value.length === 1 ? '' : 's'}`)
  if (venuePartners.value.length) items.push(`${venuePartners.value.length} partner${venuePartners.value.length === 1 ? '' : 's'}`)
  if (venueGallery.value.length) items.push(`${venueGallery.value.length} photo${venueGallery.value.length === 1 ? '' : 's'}`)
  return items.slice(0, 4)
})
const listingPreviewPrice = computed(() => {
  const prices = venueSpaces.value
    .flatMap((space) => [space?.minPrice, space?.maxPrice])
    .map((value) => String(value || '').replace(/[^\d.]/g, '').trim())
    .filter(Boolean)

  if (!prices.length) return 'Price on request'
  const numericPrices = prices.map((value) => Number(value)).filter((value) => Number.isFinite(value) && value > 0)
  if (!numericPrices.length) return 'Price on request'
  const lowest = Math.min(...numericPrices)
  return `Starts at Rs. ${lowest.toLocaleString('en-IN')}`
})
const defaultVenueSlug = computed(() =>
  String(venueForm.title || 'venue-name')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'venue-name'
)
const listingPreviewSlug = computed(() => {
  const candidate = String(venueSeoForm.slug || '').trim()
  if (candidate) {
    return candidate
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '') || 'venue-name'
  }
  return defaultVenueSlug.value
})
const partnerEditorHeading = computed(() => (isEditingPartnerRoute.value ? 'Edit vendor partner' : 'Add vendor partner'))
const packageEditorHeading = computed(() => (isEditingPackageRoute.value ? 'Edit package' : 'Add package'))
const spaceEditorHeading = computed(() => (isEditingSpaceRoute.value ? 'Edit space' : 'Add space'))
const filteredPartnerCategoryOptions = computed(() => {
  const query = partnerCategorySearchQuery.value.trim().toLowerCase()
  if (!query) return partnerCategoryOptions.value
  return partnerCategoryOptions.value.filter((item) => item.name.toLowerCase().includes(query))
})
const venueSeoPreviewSlug = computed(() => {
  const candidate = String(venueSeoForm.slug || '').trim()
  if (candidate) {
    return candidate
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '') || 'venue-name'
  }
  return defaultVenueSlug.value
})
const policyAlcoholInhouseEnabled = computed(() => String(venuePoliciesForm.alcoholInhouse || '0') === '1')
const policyAlcoholOutsideEnabled = computed(() => String(venuePoliciesForm.alcoholOutside || '0') === '1')
const showOutsideCateringFeePanel = computed(() =>
  [cateringOutsideOptions[1].value, cateringOutsideOptions[2].value].includes(venuePoliciesForm.outsideCatering)
)
const outsideCateringFeeAmountLabel = computed(() => {
  if (venuePoliciesForm.outsideCateringFeeType === 'Per plate charge') return 'Fee per plate (₹)'
  if (venuePoliciesForm.outsideCateringFeeType === 'Flat fee') return 'Flat fee (₹)'
  return ''
})
const cateringPolicySummary = computed(() => {
  const inHouse = venuePoliciesForm.inHouseCatering
  const outside = venuePoliciesForm.outsideCatering

  if (!inHouse && !outside) return null
  if (inHouse === cateringInHouseOptions[0].value && outside === cateringOutsideOptions[0].value) {
    return {
      tone: 'border-[#f2cd73] bg-[#fff7df] text-[#bf7b00]',
      text: 'Case 2: In-house catering only - outside vendors not permitted.'
    }
  }
  if (inHouse === cateringInHouseOptions[1].value && outside === cateringOutsideOptions[0].value) {
    return {
      tone: 'border-[#7edfc1] bg-[#ebfff8] text-[#247d5c]',
      text: 'Edge case: No catering at this venue - neither in-house nor outside permitted.'
    }
  }
  if (inHouse === cateringInHouseOptions[1].value && outside === cateringOutsideOptions[1].value) {
    return {
      tone: 'border-[#7edfc1] bg-[#ebfff8] text-[#247d5c]',
      text: 'Case 3: No in-house catering. Customers may bring any outside caterer.'
    }
  }
  if (inHouse === cateringInHouseOptions[1].value && outside === cateringOutsideOptions[2].value) {
    return {
      tone: 'border-[#b8cff9] bg-[#eef5ff] text-[#2d62b3]',
      text: 'Case 4: No in-house catering. Customers must choose from your approved caterer partners.'
    }
  }
  return {
    tone: 'border-[#eadfd4] bg-[#fcf7f2] text-[#6d5a4a]',
    text: `${inHouse || 'Select in-house catering'} · ${outside || 'Select outside catering policy'}`
  }
})

const normalizeCountryPhoneCode = (value) => {
  const normalized = String(value || '').trim()
  if (!normalized) return defaultCountryPhoneCode
  return countries.some((country) => country.phoneCode === normalized) ? normalized : defaultCountryPhoneCode
}

const getSortedPhoneCodes = () =>
  [...new Set(countries.map((country) => String(country.phoneCode || '').trim()).filter(Boolean))]
    .sort((left, right) => right.length - left.length)

const parseCombinedPhoneNumber = (value) => {
  const raw = String(value || '').trim()
  if (!raw) {
    return {
      phoneCode: defaultCountryPhoneCode,
      phone: ''
    }
  }

  const compact = raw.replace(/[^\d+]/g, '')
  const matchedCode = getSortedPhoneCodes().find((code) => compact.startsWith(code))

  if (matchedCode) {
    return {
      phoneCode: matchedCode,
      phone: compact.slice(matchedCode.length).replace(/\D/g, '').slice(0, 10)
    }
  }

  return {
    phoneCode: defaultCountryPhoneCode,
    phone: compact.replace(/\D/g, '').slice(0, 10)
  }
}

const syncVenuePhone = () => {
  venueForm.phone = String(venueForm.phone || '').replace(/\D/g, '').slice(0, 15)
  venueForm.phoneCode = normalizeCountryPhoneCode(venueForm.phoneCode)
}

const syncPartnerPhone = () => {
  partnerForm.phone = String(partnerForm.phone || '').replace(/\D/g, '').slice(0, 10)
  partnerForm.phoneCode = normalizeCountryPhoneCode(partnerForm.phoneCode)
}

const buildPartnerPhoneValue = () => {
  const phone = String(partnerForm.phone || '').replace(/\D/g, '').slice(0, 10)
  if (!phone) return ''
  return `${normalizeCountryPhoneCode(partnerForm.phoneCode)}${phone}`
}

const formatCoordinate = (value) => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric.toFixed(6) : ''
}

const setVenueLocationFields = ({ address = '', latitude = '', longitude = '' } = {}) => {
  venueForm.address = String(address || '').trim()
  venueForm.latitude = formatCoordinate(latitude)
  venueForm.longitude = formatCoordinate(longitude)
}

const setLocationPickerSelection = ({ address = '', latitude = '', longitude = '' } = {}) => {
  locationPickerAddress.value = String(address || '').trim()
  locationPickerLat.value = formatCoordinate(latitude)
  locationPickerLng.value = formatCoordinate(longitude)
}

const ensureLocationMarker = (lat, lng, updateView = true) => {
  if (!locationMap.value) return
  if (!locationMarker.value) {
    locationMarker.value = L.marker([lat, lng], { draggable: true }).addTo(locationMap.value)
    locationMarker.value.on('dragend', async (event) => {
      const nextLatLng = event.target.getLatLng()
      await reverseGeocodeLocation(nextLatLng.lat, nextLatLng.lng, true)
    })
  } else {
    locationMarker.value.setLatLng([lat, lng])
  }
  if (updateView) {
    locationMap.value.setView([lat, lng], Math.max(locationMap.value.getZoom(), 15))
  }
}

const reverseGeocodeLocation = async (latitude, longitude, syncMap = false) => {
  locationPickerError.value = ''
  try {
    const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(latitude)}&lon=${encodeURIComponent(longitude)}`, {
      headers: {
        Accept: 'application/json'
      }
    })
    const payload = await response.json()
    const resolvedAddress = payload?.display_name || ''
    setLocationPickerSelection({
      address: resolvedAddress,
      latitude,
      longitude
    })
    if (syncMap) ensureLocationMarker(Number(latitude), Number(longitude), false)
  } catch {
    setLocationPickerSelection({
      address: '',
      latitude,
      longitude
    })
    locationPickerError.value = 'Unable to fetch address for this point. You can still use the selected coordinates.'
  }
}

const searchLocation = async () => {
  const query = locationSearchQuery.value.trim()
  if (query.length < 3) {
    locationSearchResults.value = []
    locationPickerError.value = 'Enter at least 3 characters to search.'
    return
  }
  locationPickerError.value = ''
  locationSearchLoading.value = true
  try {
    const response = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&limit=6&q=${encodeURIComponent(query)}`, {
      headers: {
        Accept: 'application/json'
      }
    })
    const payload = await response.json()
    locationSearchResults.value = Array.isArray(payload)
      ? payload.map((item) => ({
          placeId: item?.place_id || '',
          label: item?.display_name || query,
          meta: `${Number(item?.lat).toFixed(6)}, ${Number(item?.lon).toFixed(6)}`,
          latitude: item?.lat,
          longitude: item?.lon
        }))
      : []
    if (!locationSearchResults.value.length) {
      locationPickerError.value = 'No matching locations found.'
    }
  } catch {
    locationSearchResults.value = []
    locationPickerError.value = 'Unable to search locations right now.'
  } finally {
    locationSearchLoading.value = false
  }
}

const applySearchedLocation = async (item) => {
  if (!item?.latitude || !item?.longitude) return
  locationSearchQuery.value = item.label || ''
  setLocationPickerSelection({
    address: item.label || '',
    latitude: item.latitude,
    longitude: item.longitude
  })
  ensureLocationMarker(Number(item.latitude), Number(item.longitude), true)
  await reverseGeocodeLocation(item.latitude, item.longitude, false)
}

const ensureLocationMap = async () => {
  await nextTick()
  if (!locationMap.value) {
    locationMap.value = L.map('venue-location-picker-map').setView([20.5937, 78.9629], 5)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(locationMap.value)
    locationMap.value.on('click', async (event) => {
      const { lat, lng } = event.latlng
      ensureLocationMarker(lat, lng, false)
      await reverseGeocodeLocation(lat, lng, false)
    })
  }

  const initialLat = Number(locationPickerLat.value || venueForm.latitude || 20.5937)
  const initialLng = Number(locationPickerLng.value || venueForm.longitude || 78.9629)
  const hasInitialCoords = Number.isFinite(initialLat) && Number.isFinite(initialLng)
  if (hasInitialCoords) {
    ensureLocationMarker(initialLat, initialLng, true)
  }
  locationMap.value.invalidateSize()
}

const loadCurrentLocationIntoPicker = async (forceUpdate = false) => {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    locationPickerError.value = 'Geolocation is not supported in this browser.'
    return
  }
  if (!forceUpdate && locationPickerLat.value && locationPickerLng.value) return
  locationPickerError.value = ''
  locationCurrentLoading.value = true
  try {
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000
      })
    })
    const latitude = position.coords.latitude
    const longitude = position.coords.longitude
    ensureLocationMarker(latitude, longitude, true)
    await reverseGeocodeLocation(latitude, longitude, false)
  } catch {
    locationPickerError.value = 'Unable to detect your current location. Search manually or place the pin on the map.'
  } finally {
    locationCurrentLoading.value = false
  }
}

const openLocationPicker = async () => {
  locationPickerOpen.value = true
  locationPickerError.value = ''
  locationSearchResults.value = []
  locationSearchQuery.value = venueForm.address || ''
  setLocationPickerSelection({
    address: venueForm.address,
    latitude: venueForm.latitude,
    longitude: venueForm.longitude
  })
  await ensureLocationMap()
  if (!venueForm.latitude || !venueForm.longitude) {
    await loadCurrentLocationIntoPicker()
  } else {
    ensureLocationMarker(Number(venueForm.latitude), Number(venueForm.longitude), true)
  }
}

const closeLocationPicker = () => {
  locationPickerOpen.value = false
  locationPickerError.value = ''
  if (locationMap.value) {
    locationMap.value.remove()
    locationMap.value = null
  }
  locationMarker.value = null
}

const confirmLocationPicker = () => {
  setVenueLocationFields({
    address: locationPickerAddress.value || venueForm.address,
    latitude: locationPickerLat.value || venueForm.latitude,
    longitude: locationPickerLng.value || venueForm.longitude
  })
  closeLocationPicker()
}

const bootstrapVenueLocation = async () => {
  if (moduleType.value !== 'venue') return
  if (venueForm.latitude && venueForm.longitude) return
  if (typeof navigator === 'undefined' || !navigator.geolocation) return
  try {
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000
      })
    })
    const latitude = position.coords.latitude
    const longitude = position.coords.longitude
    await reverseGeocodeLocation(latitude, longitude, false)
    setVenueLocationFields({
      address: locationPickerAddress.value,
      latitude,
      longitude
    })
  } catch {
    // Leave the fields empty when location access is denied.
  }
}

const loadModules = () => {
  if (typeof window === 'undefined') return []
  try {
    return JSON.parse(window.localStorage.getItem(storageKey.value) || '[]')
  } catch {
    return []
  }
}

const saveModules = (items) => {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(storageKey.value, JSON.stringify(items))
}

const getSpacesStorageKey = () => `business_${businessId.value}_venue_${moduleId.value || 'draft'}_spaces`

const readStoredSpaces = () => {
  if (typeof window === 'undefined' || !businessId.value) return []
  try {
    const parsed = JSON.parse(window.localStorage.getItem(getSpacesStorageKey()) || '[]')
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const populateForm = () => {
  if (!isEditMode.value) return
  const item = loadModules().find((entry) => String(entry.id) === moduleId.value)
  if (!item) return
  if (moduleType.value === 'venue') {
    const resolvedVenueType = item.type || item.venueType || ''
    Object.assign(venueForm, {
      id: item.id,
      title: item.title || item.name || '',
      type: resolveVenueTypeValue(resolvedVenueType),
      tagLine: item.tagLine || item.tagline || '',
      about: item.about || '',
      address: item.address || '',
      latitude: item.latitude || '',
      longitude: item.longitude || '',
      phoneCode: normalizeCountryPhoneCode(item.phoneCode || item.phone_code || ''),
      phone: String(item.phone || '').replace(/\D/g, '').slice(0, 15),
      email: item.email || '',
      website: item.website || '',
      instagram: item.instagram || '',
      facebook: item.facebook || '',
      youtube: item.youtube || '',
      highlights: Array.isArray(item.highlights) ? [...item.highlights] : []
    })
    Object.assign(venueDetailsForm, {
      venueType: resolveVenueTypeValue(item.venueType || item.type || ''),
      yearEstablished: item.yearEstablished || '',
      eventsHosted: Array.isArray(item.eventsHosted) ? [...item.eventsHosted] : [],
      amenities: Array.isArray(item.amenities) ? [...item.amenities] : [],
      inHouseServices: Array.isArray(item.inHouseServices) ? [...item.inHouseServices] : []
    })
    activeSection.value = 'identity'
    return
  }
  Object.assign(vendorForm, {
    id: item.id,
    category: item.category || 'Catering',
    brandName: item.brandName || '',
    tagline: item.tagline || '',
    about: item.about || '',
    city: item.city || '',
    state: item.state || '',
    phone: item.phone || '',
    email: item.email || '',
    website: item.website || '',
    highlights: Array.isArray(item.highlights) ? [...item.highlights] : [],
    catalogThemes: Array.isArray(item.catalogThemes) ? [...item.catalogThemes] : [],
    catalogStyles: Array.isArray(item.catalogStyles) ? [...item.catalogStyles] : [],
    minBudget: item.minBudget || '',
    startingPrice: item.startingPrice || '',
    customerCancellationPolicy: item.customerCancellationPolicy || 'Partial Refund',
    vendorCancellationPolicy: item.vendorCancellationPolicy || 'Full Refund',
    dateAdjustmentOffered: Boolean(item.dateAdjustmentOffered),
    cancellationTiers: Array.isArray(item.cancellationTiers) && item.cancellationTiers.length
      ? item.cancellationTiers.map((tier) => ({
          days: String(tier?.days || '').trim(),
          refund: String(tier?.refund || '').trim()
        }))
      : [
          { days: '30', refund: '100' },
          { days: '15', refund: '50' },
          { days: '7', refund: '0' }
        ],
    cancellationTerms: item.cancellationTerms || '',
    depositPercent: item.depositPercent || '25',
    pricingMode: item.pricingMode || 'packages',
    pricingUnit: item.pricingUnit || 'Per Event',
    priceRangeFrom: item.priceRangeFrom || '',
    priceRangeTo: item.priceRangeTo || '',
    packages: Array.isArray(item.packages)
      ? item.packages.map((pkg, index) => ({
          id: String(pkg?.id || `vendor-package-${index}`),
          name: pkg?.name || '',
          price: pkg?.price || '',
          description: pkg?.description || '',
          includedServices: Array.isArray(pkg?.includedServices) ? [...pkg.includedServices] : splitToArray(pkg?.includedServices),
          maxGuests: pkg?.maxGuests || '',
          validHours: pkg?.validHours || ''
        }))
      : []
  })
  activeSection.value = 'brand'
}

populateForm()

const toggleTag = (collection, value) => {
  const index = collection.indexOf(value)
  if (index === -1) {
    collection.push(value)
    return
  }
  collection.splice(index, 1)
}

const sanitizeNumericInput = (value, maxLength = 6) => String(value || '').replace(/\D/g, '').slice(0, maxLength)

const syncVendorPolicyField = (field, maxLength = 6) => {
  vendorForm[field] = sanitizeNumericInput(vendorForm[field], maxLength)
}

const syncVendorCancellationTier = (index, key, maxLength = 3) => {
  if (!vendorForm.cancellationTiers[index]) return
  vendorForm.cancellationTiers[index][key] = sanitizeNumericInput(vendorForm.cancellationTiers[index][key], maxLength)
}

const addVendorCancellationTier = () => {
  vendorForm.cancellationTiers.push({ days: '', refund: '' })
}

const removeVendorCancellationTier = (index) => {
  if (vendorForm.cancellationTiers.length <= 1) return
  vendorForm.cancellationTiers.splice(index, 1)
}

const setVenueType = (value) => {
  const normalizedValue = resolveVenueTypeValue(value)
  venueForm.type = normalizedValue
  venueDetailsForm.venueType = normalizedValue
}

const resetVenueFieldErrors = () => {
  venueFieldErrors.title = ''
  venueFieldErrors.type = ''
  venueFieldErrors.tagLine = ''
  venueFieldErrors.address = ''
  venueFieldErrors.latitude = ''
  venueFieldErrors.longitude = ''
  venueFieldErrors.phone = ''
  venueFieldErrors.email = ''
  venueFieldErrors.website = ''
}

const resetVenueDetailsErrors = () => {
  venueDetailsErrors.venueType = ''
  venueDetailsErrors.yearEstablished = ''
  venueDetailsErrors.eventsHosted = ''
}

const resetPackageFieldErrors = () => {
  packageFieldErrors.name = ''
  packageFieldErrors.price = ''
  packageFieldErrors.maxGuests = ''
  packageFieldErrors.validHours = ''
  packageFieldErrors.description = ''
  packageFieldErrors.includedServices = ''
}

const resetSpaceFieldErrors = () => {
  spaceFieldErrors.name = ''
  spaceFieldErrors.spaceType = ''
  spaceFieldErrors.areaSqft = ''
  spaceFieldErrors.seatingConfigurations = ''
  spaceFieldErrors.minPrice = ''
  spaceFieldErrors.maxPrice = ''
  spaceFieldErrors.description = ''
}

const resetPartnerFieldErrors = () => {
  partnerFieldErrors.name = ''
  partnerFieldErrors.category = ''
  partnerFieldErrors.phone = ''
}

const isValidEmail = (value) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
const isValidUrl = (value) => !value || /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/i.test(value)
const isValidCoordinate = (value) => value === '' || !Number.isNaN(Number(value))
const isValidPhoneNumber = (value) => {
  const digits = String(value || '').replace(/\D/g, '')
  return digits.length === 10
}
const isPositiveNumber = (value) => {
  const normalized = String(value || '').trim()
  if (!normalized) return false
  const numeric = Number(normalized)
  return Number.isFinite(numeric) && numeric > 0
}

const validateVenueIdentityForm = () => {
  resetVenueFieldErrors()
  let hasError = false

  if (!venueForm.title.trim()) {
    venueFieldErrors.title = 'Venue name is required.'
    hasError = true
  }
  if (!venueForm.type.trim()) {
    venueFieldErrors.type = 'Venue type is required.'
    hasError = true
  }
  if (!venueForm.tagLine.trim()) {
    venueFieldErrors.tagLine = 'Tagline is required.'
    hasError = true
  }
  if (!venueForm.address.trim()) {
    venueFieldErrors.address = 'Address is required.'
    hasError = true
  }
  if (venueForm.phone && String(venueForm.phone).replace(/\D/g, '').length < 8) {
    venueFieldErrors.phone = 'Enter a valid phone number.'
    hasError = true
  }
  if (!isValidEmail(venueForm.email)) {
    venueFieldErrors.email = 'Enter a valid email address.'
    hasError = true
  }
  if (!isValidUrl(venueForm.website)) {
    venueFieldErrors.website = 'Enter a valid website URL.'
    hasError = true
  }
  if (!isValidCoordinate(venueForm.latitude)) {
    venueFieldErrors.latitude = 'Latitude must be numeric.'
    hasError = true
  }
  if (!isValidCoordinate(venueForm.longitude)) {
    venueFieldErrors.longitude = 'Longitude must be numeric.'
    hasError = true
  }

  return !hasError
}

const validateVenueDetailsForm = () => {
  resetVenueDetailsErrors()
  let hasError = false

  if (!venueDetailsForm.venueType.trim()) {
    venueDetailsErrors.venueType = 'Venue type is required.'
    hasError = true
  }
  if (venueDetailsForm.yearEstablished && !/^\d{4}$/.test(venueDetailsForm.yearEstablished)) {
    venueDetailsErrors.yearEstablished = 'Year established must be a 4-digit year.'
    hasError = true
  }
  if (!venueDetailsForm.eventsHosted.length) {
    venueDetailsErrors.eventsHosted = 'Select at least one event type.'
    hasError = true
  }

  return !hasError
}

const validatePackageForm = () => {
  resetPackageFieldErrors()
  let hasError = false

  if (!packageForm.name.trim()) {
    packageFieldErrors.name = 'Package name is required.'
    hasError = true
  }
  if (!isPositiveNumber(packageForm.price)) {
    packageFieldErrors.price = 'Enter a valid package price.'
    hasError = true
  }
  if (!isPositiveNumber(packageForm.maxGuests) || !Number.isInteger(Number(packageForm.maxGuests))) {
    packageFieldErrors.maxGuests = 'Enter a valid guest capacity.'
    hasError = true
  }
  if (!packageForm.validHours.trim()) {
    packageFieldErrors.validHours = 'Valid hours is required.'
    hasError = true
  }
  if (!packageForm.description.trim()) {
    packageFieldErrors.description = 'Package description is required.'
    hasError = true
  }
  if (!packageForm.includedServices.length) {
    packageFieldErrors.includedServices = 'Select at least one included service.'
    hasError = true
  }

  return !hasError
}

const validateSpaceForm = () => {
  resetSpaceFieldErrors()
  let hasError = false

  if (!spaceForm.name.trim()) {
    spaceFieldErrors.name = 'Space name is required.'
    hasError = true
  }
  if (!spaceForm.spaceType.trim()) {
    spaceFieldErrors.spaceType = 'Space type is required.'
    hasError = true
  }
  if (!isPositiveNumber(spaceForm.areaSqft)) {
    spaceFieldErrors.areaSqft = 'Enter a valid area in sqft.'
    hasError = true
  }
  if (!spaceForm.seatingConfigurations.length) {
    spaceFieldErrors.seatingConfigurations = 'Select at least one seating configuration.'
    hasError = true
  } else {
    const missingCapacity = spaceForm.seatingConfigurations.some((option) => !isPositiveNumber(spaceForm.seatingCapacities?.[option]))
    if (missingCapacity) {
      spaceFieldErrors.seatingConfigurations = 'Enter a valid capacity for each selected seating configuration.'
      hasError = true
    }
  }
  if (!isPositiveNumber(spaceForm.minPrice)) {
    spaceFieldErrors.minPrice = 'Enter a valid minimum price.'
    hasError = true
  }
  if (!isPositiveNumber(spaceForm.maxPrice)) {
    spaceFieldErrors.maxPrice = 'Enter a valid maximum price.'
    hasError = true
  } else if (isPositiveNumber(spaceForm.minPrice) && Number(spaceForm.maxPrice) < Number(spaceForm.minPrice)) {
    spaceFieldErrors.maxPrice = 'Maximum price must be greater than or equal to minimum price.'
    hasError = true
  }
  if (!spaceForm.description.trim()) {
    spaceFieldErrors.description = 'Space description is required.'
    hasError = true
  }

  return !hasError
}

const validatePartnerForm = () => {
  resetPartnerFieldErrors()
  let hasError = false

  if (!partnerForm.name.trim()) {
    partnerFieldErrors.name = 'Partner name is required.'
    hasError = true
  }
  if (!partnerForm.category.trim()) {
    partnerFieldErrors.category = 'Category is required.'
    hasError = true
  }
  syncPartnerPhone()
  if (!isValidPhoneNumber(partnerForm.phone)) {
    partnerFieldErrors.phone = 'Enter a valid 10-digit phone number.'
    hasError = true
  }

  return !hasError
}

const splitToArray = (value) =>
  Array.isArray(value)
    ? value.map((entry) => String(entry || '').trim()).filter(Boolean)
    : String(value || '').split(',').map((entry) => entry.trim()).filter(Boolean)

const parseSeatConfigEntries = (value) => {
  const entries = splitToArray(value)
  const configurations = []
  const capacities = {}

  entries.forEach((entry) => {
    const [rawLabel, rawCapacity = ''] = String(entry).split(':')
    const label = String(rawLabel || '').trim()
    if (!label) return
    configurations.push(label)
    const digits = String(rawCapacity || '').replace(/\D/g, '').slice(0, 6)
    if (digits) capacities[label] = digits
  })

  return {
    configurations,
    capacities
  }
}

const buildSeatConfigPayload = () =>
  spaceForm.seatingConfigurations.join(',')

const buildSeatCapacityPayload = () =>
  spaceForm.seatingConfigurations
    .map((option) => String(spaceForm.seatingCapacities?.[option] || '').replace(/\D/g, '').slice(0, 6))
    .filter(Boolean)
    .join(',')

const normalizeApiCollection = (data, keys = []) => {
  const payload = data?.data || data || {}
  if (Array.isArray(payload)) return payload
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key]
  }
  return []
}

const packageServicesPreviewLimit = 4

const isPackageServicesExpanded = (packageId) => Boolean(expandedPackageServices.value[String(packageId)])

const visiblePackageServices = (pkg) => {
  const services = Array.isArray(pkg?.includedServices) ? pkg.includedServices : []
  return isPackageServicesExpanded(pkg?.id) ? services : services.slice(0, packageServicesPreviewLimit)
}

const hiddenPackageServicesCount = (pkg) => {
  const services = Array.isArray(pkg?.includedServices) ? pkg.includedServices : []
  return Math.max(services.length - packageServicesPreviewLimit, 0)
}

const togglePackageServices = (packageId) => {
  const key = String(packageId || '')
  if (!key) return
  expandedPackageServices.value = {
    ...expandedPackageServices.value,
    [key]: !expandedPackageServices.value[key]
  }
}

const normalizeVenueGalleryEntries = (source) => {
  const items = Array.isArray(source) ? source : [source]
  return items
    .flatMap((item, itemIndex) => {
      if (!item) return []
      if (typeof item === 'string') {
        return [{
          id: `gallery-${itemIndex}-${item}`,
          src: item
        }]
      }
      const imageKeys = ['image1', 'image2', 'image3', 'image4', 'image', 'image_url', 'url', 'path']
      const entryId = String(item?.id || item?.gallery_id || item?.venue_gallery_id || item?.img_id || '')
      return imageKeys
        .map((key, keyIndex) => {
          const src = item?.[key] || ''
          if (!src) return null
          return {
            id: entryId || `gallery-${itemIndex}-${keyIndex}-${src}`,
            src,
            tagName: String(item?.tag_name || item?.tagName || item?.name || '').trim(),
            isCover: String(item?.is_cover || item?.isCover || '0') === '1'
          }
        })
        .filter(Boolean)
    })
    .filter((entry) => entry?.src)
}

const resetPackageForm = () => {
  editingPackageId.value = ''
  resetPackageFieldErrors()
  packageForm.name = ''
  packageForm.price = ''
  packageForm.description = ''
  packageForm.includedServices = []
  packageForm.maxGuests = ''
  packageForm.validHours = ''
}

const closePackageForm = () => {
  if (isPackageEditorRoute.value) {
    resetPackageForm()
    packageFormVisible.value = false
    closePackageEditorRoute()
    return
  }
  packageFormVisible.value = false
  resetPackageForm()
}

const editPackage = async (packageId) => {
  if (isPackageEditorRoute.value) return
  openPackageEditPage(packageId)
}

const hydrateVendorPackageForm = (packageId) => {
  const item = vendorForm.packages.find((entry) => String(entry.id) === String(packageId))
  if (!item) return false
  editingPackageId.value = String(item.id)
  packageForm.name = item.name || ''
  packageForm.price = item.price || ''
  packageForm.description = item.description || ''
  packageForm.includedServices = Array.isArray(item.includedServices) ? [...item.includedServices] : []
  packageForm.maxGuests = item.maxGuests || ''
  packageForm.validHours = item.validHours || ''
  packageFormVisible.value = true
  return true
}

const hydratePackageForm = async (packageId) => {
  if (moduleType.value === 'vendor') return hydrateVendorPackageForm(packageId)
  if (!packageId) return
  error.value = ''
  try {
    const formData = new FormData()
    formData.append('package_id', packageId)
    const { data } = await api.editPackageVenue(formData)
    const item = normalizeApiCollection(data, ['package', 'packages', 'items', 'list'])[0] || data?.data || data || {}
    editingPackageId.value = String(item?.package_id || item?.id || packageId)
    packageForm.name = item?.name || ''
    packageForm.price = item?.price || ''
    packageForm.description = item?.description || ''
    packageForm.includedServices = splitToArray(item?.included_services || item?.includedServices)
    packageForm.maxGuests = item?.max_guests || item?.maxGuests || ''
    packageForm.validHours = item?.valid_hours || item?.validHours || ''
    packageFormVisible.value = true
    return true
  } catch (err) {
    error.value = err?.message || 'Failed to load package details'
    await showErrorToast(error.value)
    return false
  }
}

const saveVendorPackage = async () => {
  const packageId = editingPackageId.value || `vendor-package-${Date.now()}`
  const nextPackage = {
    id: String(packageId),
    name: packageForm.name || '',
    price: packageForm.price || '',
    description: packageForm.description || '',
    includedServices: [...packageForm.includedServices],
    maxGuests: packageForm.maxGuests || '',
    validHours: packageForm.validHours || ''
  }
  const packageIndex = vendorForm.packages.findIndex((item) => String(item.id) === String(packageId))
  if (packageIndex === -1) {
    vendorForm.packages.unshift(nextPackage)
  } else {
    vendorForm.packages.splice(packageIndex, 1, nextPackage)
  }
}

const resetPartnerForm = () => {
  editingPartnerId.value = ''
  resetPartnerFieldErrors()
  partnerCategoryDropdownOpen.value = false
  partnerCategorySearchQuery.value = ''
  partnerForm.name = ''
  partnerForm.category = ''
  partnerForm.phoneCode = defaultCountryPhoneCode
  partnerForm.phone = ''
}

const closePartnerForm = () => {
  if (isPartnerEditorRoute.value) {
    resetPartnerForm()
    partnerFormVisible.value = false
    closePartnerEditorRoute()
    return
  }
  partnerFormVisible.value = false
  resetPartnerForm()
}

const editPartner = (partnerId) => {
  if (isPartnerEditorRoute.value) return
  openPartnerEditPage(partnerId)
}

const hydratePartnerForm = (partnerId) => {
  const item = venuePartners.value.find((entry) => String(entry.id) === String(partnerId))
  if (!item) return false
  editingPartnerId.value = String(item.id)
  partnerForm.name = item.name || ''
  partnerForm.category = resolvePartnerCategoryValue(item.category || item.categoryLabel || '')
  partnerForm.phoneCode = normalizeCountryPhoneCode(item.phoneCode || defaultCountryPhoneCode)
  partnerForm.phone = String(item.phone || '').replace(/\D/g, '').slice(0, 10)
  partnerCategoryDropdownOpen.value = false
  partnerCategorySearchQuery.value = ''
  partnerFormVisible.value = true
  return true
}

const togglePartnerCategoryDropdown = () => {
  partnerCategoryDropdownOpen.value = !partnerCategoryDropdownOpen.value
  if (partnerCategoryDropdownOpen.value) {
    partnerCategorySearchQuery.value = ''
  }
}

const selectPartnerCategory = (value) => {
  partnerForm.category = String(value?.id || value || '').trim()
  partnerCategoryDropdownOpen.value = false
  partnerCategorySearchQuery.value = ''
}

const clearVenueGalleryAutoUploadTimer = () => {
  if (venueGalleryAutoUploadTimer.value) {
    clearTimeout(venueGalleryAutoUploadTimer.value)
    venueGalleryAutoUploadTimer.value = null
  }
}

const handleVenueGalleryFiles = async (items) => {
  venueGalleryFiles.value = items
  clearVenueGalleryAutoUploadTimer()
  if (!items.length) return
  if (!currentVenueId.value) {
    venueGalleryFiles.value = []
    await showErrorToast('Save venue identity first before uploading gallery images.')
    return
  }
  if (!uploadableVenueGalleryTag.value) {
    venueGalleryFiles.value = []
    await showErrorToast('Select a specific gallery category first.')
    return
  }
  venueGalleryAutoUploadTimer.value = setTimeout(() => {
    venueGalleryAutoUploadTimer.value = null
    if (venueGalleryUploading.value || !venueGalleryFiles.value.length) return
    saveGallery()
  }, 250)
}

const normalizeGalleryCategory = (item, index) => ({
  id: String(item?.tag_id || item?.id || item?.tag || index + 1),
  label: String(item?.name || item?.title || item?.label || item?.tag_name || `Category ${index + 1}`)
})

const selectVenueGalleryCategory = async (tagId) => {
  activeVenueGalleryTag.value = String(tagId || '')
  await loadVenueGallery()
}

const resetSpaceForm = () => {
  editingSpaceId.value = ''
  resetSpaceFieldErrors()
  spaceForm.name = ''
  spaceForm.spaceType = ''
  spaceForm.setting = 'Indoor'
  spaceForm.areaSqft = ''
  spaceForm.seatingConfigurations = []
  spaceForm.seatingCapacities = {}
  spaceForm.minPrice = ''
  spaceForm.maxPrice = ''
  spaceForm.amenities = []
  spaceForm.description = ''
}

const closeSpaceForm = () => {
  if (isSpaceEditorRoute.value) {
    resetSpaceForm()
    closeSpaceEditorRoute()
    return
  }
  spaceFormVisible.value = false
  resetSpaceForm()
}

const editSpace = (spaceId) => {
  if (isSpaceEditorRoute.value) return
  openSpaceEditPage(spaceId)
}

const hydrateSpaceForm = (spaceId) => {
  const item = venueSpaces.value.find((space) => String(space.id) === String(spaceId))
  if (!item) return false
  const seatConfig = parseSeatConfigEntries(item.rawSeatConfig || item.seatingConfigurations)
  const mappedCapacities = {}
  if (Array.isArray(item.seatingCapacities) && item.seatingCapacities.length) {
    seatConfig.configurations.forEach((option, index) => {
      const digits = String(item.seatingCapacities[index] || '').replace(/\D/g, '').slice(0, 6)
      if (digits) mappedCapacities[option] = digits
    })
  }
  editingSpaceId.value = item.id
  spaceForm.name = item.name || ''
  spaceForm.spaceType = resolveSpaceTypeValue(item.spaceType || item.spaceTypeLabel || '')
  spaceForm.setting = item.setting || 'Indoor'
  spaceForm.areaSqft = item.areaSqft || ''
  spaceForm.seatingConfigurations = [...seatConfig.configurations]
  spaceForm.seatingCapacities = Object.keys(mappedCapacities).length ? mappedCapacities : { ...seatConfig.capacities }
  spaceForm.minPrice = item.minPrice || ''
  spaceForm.maxPrice = item.maxPrice || ''
  spaceForm.amenities = Array.isArray(item.amenities) ? [...item.amenities] : []
  spaceForm.description = item.description || ''
  spaceFormVisible.value = true
  return true
}

const handleSeatConfigToggle = (option) => {
  if (spaceForm.seatingConfigurations.includes(option)) {
    if (!spaceForm.seatingCapacities[option]) {
      spaceForm.seatingCapacities[option] = ''
    }
    return
  }
  delete spaceForm.seatingCapacities[option]
}

const syncSeatConfigCapacity = (option) => {
  if (!spaceForm.seatingConfigurations.includes(option)) return
  spaceForm.seatingCapacities[option] = String(spaceForm.seatingCapacities[option] || '').replace(/\D/g, '').slice(0, 6)
}

const saveSpace = () => {
  error.value = ''
  if (!currentVenueId.value) {
    error.value = 'Save venue identity first before adding spaces.'
    showErrorToast(error.value)
    return
  }
  if (!validateSpaceForm()) {
    error.value = 'Fix the highlighted space fields and try again.'
    showErrorToast(error.value)
    return
  }
  saving.value = true
  const formData = new FormData()
  formData.append('venue_id', currentVenueId.value)
  if (editingSpaceId.value) formData.append('space_id', editingSpaceId.value)
  formData.append('name', spaceForm.name || '')
  formData.append('type', spaceForm.spaceType || '')
  formData.append('setting', spaceForm.setting || '')
  formData.append('area_sqft', spaceForm.areaSqft || '')
  formData.append('seat_config', buildSeatConfigPayload())
  formData.append('seat_capacity', buildSeatCapacityPayload())
  formData.append('min_price', spaceForm.minPrice || '')
  formData.append('max_price', spaceForm.maxPrice || '')
  formData.append('amenities', spaceForm.amenities.join(','))
  formData.append('description', spaceForm.description || '')

  const wasEditing = Boolean(editingSpaceId.value)
  const request = editingSpaceId.value ? api.updateVenueSpace(formData) : api.addVenueSpace(formData)

  request
    .then(async (response) => {
      const data = response?.data || {}
      const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
      if (isError) throw new Error(data?.msg || data?.message || 'Failed to save space')
      await loadVenueSpaces()
      const savedSpaceId = String(data?.space_id || data?.id || data?.data?.space_id || editingSpaceId.value || '')
      resetSpaceForm()
      if (isSpaceEditorRoute.value) {
        await showSuccessToast(wasEditing ? 'Space updated successfully' : 'Space added successfully')
        await router.push({
          name: 'business-module-edit',
          params: {
            businessId: businessId.value,
            moduleType: moduleType.value,
            moduleId: currentVenueId.value
          },
          query: {
            businessName: businessName.value,
            section: 'spaces',
            savedSpaceId
          }
        })
        return
      }
      closeSpaceForm()
      await showSuccessToast('Space saved successfully')
    })
    .catch((err) => {
      error.value = err?.message || 'Failed to save space'
      showErrorToast(error.value)
    })
    .finally(() => {
      saving.value = false
    })
}

const deleteSpace = async (spaceId) => {
  const targetId = String(spaceId || '').trim()
  if (!targetId) return

  const targetSpace = venueSpaces.value.find((space) => String(space.id) === targetId)
  const result = await Swal.fire({
    title: 'Delete space?',
    text: `This will permanently remove ${targetSpace?.name || 'this space'}.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#dc2626',
    reverseButtons: true
  })

  if (!result.isConfirmed) return

  deletingSpaceId.value = targetId
  error.value = ''

  try {
    const formData = new FormData()
    formData.append('space_id', targetId)
    formData.append('venue_id', currentVenueId.value)
    const { data } = await api.deleteVenueSpace(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) throw new Error(data?.msg || data?.message || 'Failed to delete space')
    await loadVenueSpaces()
    if (editingSpaceId.value === targetId) closeSpaceForm()
    await showSuccessToast('Space deleted successfully')
  } catch (err) {
    error.value = err?.message || 'Failed to delete space'
    await showErrorToast(error.value)
  } finally {
    deletingSpaceId.value = ''
  }
}

const goBack = () => {
  router.push({
    name: 'business-edit',
    params: { id: businessId.value },
    query: { tab: moduleType.value }
  })
}

const openSpaceCreatePage = () => {
  if (!currentVenueId.value) return
  router.push({
    name: 'business-module-edit',
    params: {
      businessId: businessId.value,
      moduleType: moduleType.value,
      moduleId: currentVenueId.value
    },
    query: {
      businessName: businessName.value,
      section: 'spaces',
      spaceMode: 'new'
    }
  })
}

const openSpaceEditPage = (spaceId) => {
  if (!currentVenueId.value) return
  router.push({
    name: 'business-module-edit',
    params: {
      businessId: businessId.value,
      moduleType: moduleType.value,
      moduleId: currentVenueId.value
    },
    query: {
      businessName: businessName.value,
      section: 'spaces',
      spaceMode: 'edit',
      spaceId: String(spaceId || '')
    }
  })
}

const closeSpaceEditorRoute = () => {
  router.push({
    name: 'business-module-edit',
    params: {
      businessId: businessId.value,
      moduleType: moduleType.value,
      moduleId: currentVenueId.value || moduleId.value
    },
    query: {
      businessName: businessName.value,
      section: 'spaces'
    }
  })
}

const openPackageCreatePage = () => {
  const targetId = moduleType.value === 'venue' ? currentVenueId.value : moduleId.value
  if (!targetId) return
  router.push({
    name: 'business-module-edit',
    params: {
      businessId: businessId.value,
      moduleType: moduleType.value,
      moduleId: targetId
    },
    query: {
      businessName: businessName.value,
      section: moduleType.value === 'venue' ? 'packages' : 'pricing',
      packageMode: 'new'
    }
  })
}

const openPartnerCreatePage = () => {
  if (!currentVenueId.value) return
  router.push({
    name: 'business-module-edit',
    params: {
      businessId: businessId.value,
      moduleType: moduleType.value,
      moduleId: currentVenueId.value
    },
    query: {
      businessName: businessName.value,
      section: 'partners',
      partnerMode: 'new'
    }
  })
}

const openPartnerEditPage = (partnerId) => {
  if (!currentVenueId.value) return
  router.push({
    name: 'business-module-edit',
    params: {
      businessId: businessId.value,
      moduleType: moduleType.value,
      moduleId: currentVenueId.value
    },
    query: {
      businessName: businessName.value,
      section: 'partners',
      partnerMode: 'edit',
      partnerId: String(partnerId || '')
    }
  })
}

const closePartnerEditorRoute = () => {
  router.push({
    name: 'business-module-edit',
    params: {
      businessId: businessId.value,
      moduleType: moduleType.value,
      moduleId: currentVenueId.value || moduleId.value
    },
    query: {
      businessName: businessName.value,
      section: 'partners'
    }
  })
}

const openPackageEditPage = (packageId) => {
  const targetId = moduleType.value === 'venue' ? currentVenueId.value : moduleId.value
  if (!targetId) return
  router.push({
    name: 'business-module-edit',
    params: {
      businessId: businessId.value,
      moduleType: moduleType.value,
      moduleId: targetId
    },
    query: {
      businessName: businessName.value,
      section: moduleType.value === 'venue' ? 'packages' : 'pricing',
      packageMode: 'edit',
      packageId: String(packageId || '')
    }
  })
}

const closePackageEditorRoute = () => {
  const targetId = moduleType.value === 'venue' ? (currentVenueId.value || moduleId.value) : moduleId.value
  router.push({
    name: 'business-module-edit',
    params: {
      businessId: businessId.value,
      moduleType: moduleType.value,
      moduleId: targetId
    },
    query: {
      businessName: businessName.value,
      section: moduleType.value === 'venue' ? 'packages' : 'pricing'
    }
  })
}

const openListingPreviewPage = () => {
  if (!currentVenueId.value) return
  router.push({
    name: 'business-venue-detail',
    params: {
      id: businessId.value,
      venueId: currentVenueId.value
    },
    query: {
      businessName: businessName.value,
      venueTitle: venueForm.title || 'Venue detail',
      venueAddress: venueForm.address || '',
      preview: '1'
    }
  })
}

const buildPayload = () => {
  if (moduleType.value === 'venue') {
    return {
      id: currentVenueId.value || String(Date.now()),
      name: venueForm.title || 'Untitled venue',
      title: venueForm.title || 'Untitled venue',
      type: venueForm.type || '',
      tagLine: venueForm.tagLine || '',
      about: venueForm.about || '',
      address: venueForm.address || '',
      latitude: venueForm.latitude || '',
      longitude: venueForm.longitude || '',
      phoneCode: venueForm.phoneCode || defaultCountryPhoneCode,
      phone: venueForm.phone || '',
      email: venueForm.email || '',
      website: venueForm.website || '',
      instagram: venueForm.instagram || '',
      facebook: venueForm.facebook || '',
      youtube: venueForm.youtube || '',
      highlights: [...venueForm.highlights],
      venueType: venueDetailsForm.venueType || '',
      yearEstablished: venueDetailsForm.yearEstablished || '',
      eventsHosted: [...venueDetailsForm.eventsHosted],
      amenities: [...venueDetailsForm.amenities],
      inHouseServices: [...venueDetailsForm.inHouseServices],
      spaces: venueSpaces.value.length,
      packages: venuePackages.value.length,
      partners: venuePartners.value.length,
      status: 'Draft',
      progress: 13
    }
  }
  return {
    id: moduleId.value || String(Date.now()),
    brandName: vendorForm.brandName || 'Untitled vendor',
    category: vendorForm.category || 'No category',
    tagline: vendorForm.tagline || '',
    about: vendorForm.about || '',
    city: vendorForm.city || '',
    state: vendorForm.state || '',
    phone: vendorForm.phone || '',
    email: vendorForm.email || '',
    website: vendorForm.website || '',
    highlights: [...vendorForm.highlights],
    catalogThemes: [...vendorForm.catalogThemes],
    catalogStyles: [...vendorForm.catalogStyles],
    minBudget: vendorForm.minBudget || '',
    startingPrice: vendorForm.startingPrice || '',
    customerCancellationPolicy: vendorForm.customerCancellationPolicy || 'Partial Refund',
    vendorCancellationPolicy: vendorForm.vendorCancellationPolicy || 'Full Refund',
    dateAdjustmentOffered: Boolean(vendorForm.dateAdjustmentOffered),
    cancellationTiers: vendorForm.cancellationTiers.map((tier) => ({
      days: String(tier?.days || '').trim(),
      refund: String(tier?.refund || '').trim()
    })),
    cancellationTerms: vendorForm.cancellationTerms || '',
    depositPercent: vendorForm.depositPercent || '',
    pricingMode: vendorForm.pricingMode || 'packages',
    pricingUnit: vendorForm.pricingUnit || 'Per Event',
    priceRangeFrom: vendorForm.priceRangeFrom || '',
    priceRangeTo: vendorForm.priceRangeTo || '',
    packages: vendorForm.packages.map((pkg, index) => ({
      id: String(pkg?.id || `vendor-package-${index}`),
      name: pkg?.name || '',
      price: pkg?.price || '',
      description: pkg?.description || '',
      includedServices: Array.isArray(pkg?.includedServices) ? [...pkg.includedServices] : [],
      maxGuests: pkg?.maxGuests || '',
      validHours: pkg?.validHours || ''
    })),
    status: 'Draft',
    progress: 0
  }
}

const normalizeHighlightOption = (item) => {
  if (typeof item === 'string') return item.trim()
  return String(item?.title || item?.name || item?.highlight || item?.label || '').trim()
}

const normalizeOptionValue = (item) => {
  if (typeof item === 'string') return item.trim()
  return String(item?.title || item?.name || item?.label || item?.value || item?.type || '').trim()
}

const normalizeVenueTypeOption = (item) => {
  if (typeof item === 'string') {
    const name = item.trim()
    if (!name) return null
    return {
      id: name,
      name
    }
  }

  const name = String(item?.name || item?.title || item?.label || item?.value || item?.type || '').trim()
  const id = String(item?.id || item?.venue_type_id || item?.type_id || name).trim()

  if (!name || !id) return null

  return {
    id,
    name
  }
}

const normalizePartnerCategoryOption = (item) => {
  if (typeof item === 'string') {
    const name = item.trim()
    if (!name) return null
    return {
      id: name,
      name
    }
  }

  const name = String(item?.name || item?.title || item?.label || item?.value || item?.type || '').trim()
  const id = String(item?.id || item?.category_id || item?.vendor_category_id || name).trim()

  if (!name || !id) return null

  return {
    id,
    name
  }
}

const normalizeSpaceTypeOption = (item) => {
  if (typeof item === 'string') {
    const name = item.trim()
    if (!name) return null
    return {
      id: name,
      name
    }
  }

  const name = String(item?.name || item?.title || item?.label || item?.value || item?.type || '').trim()
  const id = String(item?.id || item?.space_type_id || item?.type_id || name).trim()

  if (!name || !id) return null

  return {
    id,
    name
  }
}

const getPartnerCategoryLabel = (value) => {
  const normalized = String(value || '').trim()
  if (!normalized) return ''

  const match = partnerCategoryOptions.value.find((item) =>
    String(item?.id || '').trim() === normalized
    || String(item?.name || '').trim().toLowerCase() === normalized.toLowerCase()
  )

  return match?.name || normalized
}

const resolvePartnerCategoryValue = (value) => {
  const normalized = String(value || '').trim()
  if (!normalized) return ''

  const match = partnerCategoryOptions.value.find((item) =>
    String(item?.id || '').trim() === normalized
    || String(item?.name || '').trim().toLowerCase() === normalized.toLowerCase()
  )

  return match?.id || normalized
}

const getSpaceTypeLabel = (value) => {
  const normalized = String(value || '').trim()
  if (!normalized) return ''

  const match = venueSpaceTypeOptions.value.find((item) =>
    String(item?.id || '').trim() === normalized
    || String(item?.name || '').trim().toLowerCase() === normalized.toLowerCase()
  )

  return match?.name || normalized
}

const resolveSpaceTypeValue = (value) => {
  const normalized = String(value || '').trim()
  if (!normalized) return ''

  const match = venueSpaceTypeOptions.value.find((item) =>
    String(item?.id || '').trim() === normalized
    || String(item?.name || '').trim().toLowerCase() === normalized.toLowerCase()
  )

  return match?.id || normalized
}

function getVenueTypeLabel (value) {
  const normalized = String(value || '').trim()
  if (!normalized) return ''

  const match = venueTypeOptions.value.find((item) =>
    String(item?.id || '').trim() === normalized
    || String(item?.name || '').trim().toLowerCase() === normalized.toLowerCase()
  )

  return match?.name || normalized
}

function resolveVenueTypeValue (value) {
  const normalized = String(value || '').trim()
  if (!normalized) return ''

  const match = venueTypeOptions.value.find((item) =>
    String(item?.id || '').trim() === normalized
    || String(item?.name || '').trim().toLowerCase() === normalized.toLowerCase()
  )

  return match?.id || normalized
}

const populateVenueModuleFromApi = (item = {}) => {
  const resolvedVenueType = resolveVenueTypeValue(item?.type || item?.venue_type || item?.venue_type_id || '')
  Object.assign(venueForm, {
    id: item?.id || item?.venue_id || moduleId.value,
    title: item?.title || item?.name || '',
    type: resolvedVenueType,
    tagLine: item?.tag_line || item?.tagLine || item?.tagline || '',
    about: item?.about || '',
    address: item?.address || '',
    latitude: item?.latitude || '',
    longitude: item?.longitude || '',
    phoneCode: normalizeCountryPhoneCode(item?.phone_code || ''),
    phone: String(item?.phone || '').replace(/\D/g, '').slice(0, 15),
    email: item?.email || '',
    website: item?.website || '',
    instagram: item?.instagram || '',
    facebook: item?.facebook || '',
    youtube: item?.youtube || '',
    highlights: String(item?.highlights || '').split(',').map((entry) => entry.trim()).filter(Boolean)
  })

  Object.assign(venueDetailsForm, {
    venueType: resolveVenueTypeValue(item?.venue_type || item?.type || item?.venue_type_id || ''),
    yearEstablished: item?.est_year || '',
    eventsHosted: String(item?.events_hosted || '').split(',').map((entry) => entry.trim()).filter(Boolean),
    amenities: String(item?.amenities || '').split(',').map((entry) => entry.trim()).filter(Boolean),
    inHouseServices: String(item?.in_house_services || '').split(',').map((entry) => entry.trim()).filter(Boolean)
  })
}

const extractArrayPayload = (data, keys = []) => {
  const payload = data?.data || data || {}
  if (Array.isArray(payload)) return payload
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key]
  }
  return []
}

const inferCateringInHouseValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (!normalized) return ''
  if (normalized.includes('no') || normalized.includes('not') || normalized.includes('do not')) return cateringInHouseOptions[1].value
  return cateringInHouseOptions[0].value
}

const inferOutsideCateringValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (!normalized) return ''
  if (normalized.includes('partner') || normalized.includes('panel') || normalized.includes('approved')) return cateringOutsideOptions[2].value
  if (normalized.includes('free') || normalized.includes('any') || normalized.includes('welcome') || normalized.includes('allowed')) return cateringOutsideOptions[1].value
  return cateringOutsideOptions[0].value
}

const inferDecorPolicyValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (!normalized) return ''
  if (normalized.includes('outside') && normalized.includes('allow') && normalized.includes('in-house')) return 'In-house + outside allowed'
  if (normalized.includes('outside')) return 'Outside decorators permitted'
  if (normalized.includes('panel') || normalized.includes('enlisted')) return 'Enlisted panel decorators only'
  if (normalized.includes('in-house')) return 'In-house decor only'
  return String(value || '').trim()
}

const inferMusicPolicyValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (!normalized) return ''
  if (normalized.includes('in-house') && normalized.includes('outside') && normalized.includes('permit')) return 'In-house DJ + outside permitted'
  if (normalized.includes('in-house') && normalized.includes('outside not')) return 'In-house DJ only - outside not permitted'
  if (normalized.includes('no in-house') && normalized.includes('outside permitted')) return 'No in-house DJ - outside permitted'
  if (normalized.includes('no dj') || normalized.includes('outside not permitted either')) return 'No DJ services - outside not permitted either'
  return String(value || '').trim()
}

const inferBinaryToggleValue = (value) => {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (!normalized) return '0'
  if (['1', 'true', 'yes', 'on'].includes(normalized)) return '1'
  if (/available|allow|permit|provided|licensed|bring|outside/.test(normalized)) return '1'
  return '0'
}

const fetchVenueHighlights = async () => {
  if (moduleType.value !== 'venue') return
  try {
    const { data } = await api.getVenueHighlights(new FormData())
    const payload = data?.data || data || {}
    const collection = Array.isArray(payload)
      ? payload
      : payload?.highlights || payload?.items || payload?.list || []
    const normalized = Array.isArray(collection)
      ? collection.map(normalizeHighlightOption).filter(Boolean)
      : []
    venueHighlightOptions.value = normalized.length ? normalized : [...sharedHighlightOptions]
  } catch {
    venueHighlightOptions.value = [...sharedHighlightOptions]
  }
}

const fetchVenueDetailOptions = async () => {
  if (moduleType.value !== 'venue') return
  try {
    const [typesResponse, eventsResponse, amenitiesResponse, servicesResponse, serviceVendorCategoriesResponse, spaceTypesResponse, seatConfigResponse] = await Promise.all([
      api.getVenueTypes(new FormData()),
      api.getVenueEventsHosted(new FormData()),
      api.getVenueAmenities(new FormData()),
      api.getVenueInhouseServices(new FormData()),
      api.getServiceVendorCategories(new FormData()),
      api.getVenueSpaceTypes(new FormData()),
      api.getVenueSpaceSeatConfig(new FormData())
    ])

    venueTypeOptions.value = extractArrayPayload(typesResponse.data, ['types', 'venue_types', 'items', 'list']).map(normalizeVenueTypeOption).filter(Boolean)
    venueEventsHostedOptions.value = extractArrayPayload(eventsResponse.data, ['events', 'venue_events_hosted', 'items', 'list']).map(normalizeOptionValue).filter(Boolean)
    venueAmenitiesOptions.value = extractArrayPayload(amenitiesResponse.data, ['amenities', 'venue_amenities', 'items', 'list']).map(normalizeOptionValue).filter(Boolean)
    venueInHouseServiceOptions.value = extractArrayPayload(servicesResponse.data, ['services', 'inhouse_services', 'venue_inhouse_services', 'items', 'list']).map(normalizeOptionValue).filter(Boolean)
    partnerCategoryOptions.value = extractArrayPayload(serviceVendorCategoriesResponse.data, ['categories', 'service_vendor_categories', 'vendor_categories', 'items', 'list']).map(normalizePartnerCategoryOption).filter(Boolean)
    venueSpaceTypeOptions.value = extractArrayPayload(spaceTypesResponse.data, ['types', 'space_types', 'items', 'list']).map(normalizeSpaceTypeOption).filter(Boolean)
    venueSpaceSeatConfigOptions.value = extractArrayPayload(seatConfigResponse.data, ['seat_config', 'seat_configs', 'items', 'list']).map(normalizeOptionValue).filter(Boolean)
  } catch {
    venueTypeOptions.value = venueTypeOptions.value.length
      ? venueTypeOptions.value
      : [
          { id: 'Banquet hall', name: 'Banquet hall' },
          { id: 'Resort', name: 'Resort' },
          { id: 'Farmhouse', name: 'Farmhouse' },
          { id: 'Hotel', name: 'Hotel' },
          { id: 'Convention center', name: 'Convention center' },
          { id: 'Outdoor lawn', name: 'Outdoor lawn' }
        ]
    venueEventsHostedOptions.value = venueEventsHostedOptions.value.length ? venueEventsHostedOptions.value : ['Wedding', 'Corporate', 'Birthday', 'Anniversary', 'Conference', 'Reception']
    venueAmenitiesOptions.value = venueAmenitiesOptions.value.length ? venueAmenitiesOptions.value : ['Valet Parking', 'Ample Parking', 'Power Backup', 'Wheelchair Accessible']
    venueInHouseServiceOptions.value = venueInHouseServiceOptions.value.length ? venueInHouseServiceOptions.value : ['Catering', 'Decoration', 'DJ / Music', 'Photography / Videography']
    partnerCategoryOptions.value = partnerCategoryOptions.value.length
      ? partnerCategoryOptions.value
      : [
          { id: '1', name: 'Catering' },
          { id: '2', name: 'Decoration' },
          { id: '3', name: 'Photography' },
          { id: '4', name: 'Entertainment' },
          { id: 'planning', name: 'Planning' },
          { id: 'rental', name: 'Rental' },
          { id: 'other', name: 'Other' }
        ]
    venueSpaceTypeOptions.value = venueSpaceTypeOptions.value.length
      ? venueSpaceTypeOptions.value
      : [
          { id: 'banquet-hall', name: 'Banquet Hall' },
          { id: 'lawn', name: 'Lawn' },
          { id: 'terrace', name: 'Terrace' },
          { id: 'poolside', name: 'Poolside' },
          { id: 'conference-hall', name: 'Conference Hall' }
        ]
    venueSpaceSeatConfigOptions.value = venueSpaceSeatConfigOptions.value.length ? venueSpaceSeatConfigOptions.value : ['Banquet / Rounds', 'Theatre / Auditorium', 'Cocktail / Reception']
  }
}

const loadVenuePackages = async () => {
  if (moduleType.value !== 'venue' || !currentVenueId.value) {
    venuePackages.value = []
    return
  }
  try {
    const formData = new FormData()
    formData.append('venue_id', currentVenueId.value)
    const { data } = await api.getPackagesVenue(formData)
    const items = normalizeApiCollection(data, ['packages', 'items', 'list'])
    venuePackages.value = items.map((item, index) => ({
      id: item?.package_id || item?.id || `package-${index}`,
      name: item?.name || 'Untitled package',
      price: item?.price || '',
      description: item?.description || '',
      includedServices: splitToArray(item?.included_services || item?.includedServices),
      maxGuests: item?.max_guests || item?.maxGuests || '',
      validHours: item?.valid_hours || item?.validHours || ''
    }))
  } catch {
    venuePackages.value = []
  }
}

const saveVenuePackage = async () => {
  if (!currentVenueId.value) throw new Error('Save venue identity first before adding packages.')
  const formData = new FormData()
  if (editingPackageId.value) {
    formData.append('package_id', editingPackageId.value)
  } else {
    formData.append('venue_id', currentVenueId.value)
  }
  formData.append('name', packageForm.name || '')
  formData.append('price', packageForm.price || '')
  formData.append('description', packageForm.description || '')
  formData.append('included_services', packageForm.includedServices.join(','))
  formData.append('max_guests', packageForm.maxGuests || '')
  formData.append('valid_hours', packageForm.validHours || '')
  const response = editingPackageId.value ? await api.updatePackageVenue(formData) : await api.addPackageVenue(formData)
  const data = response?.data || {}
  const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
  if (isError) throw new Error(data?.msg || data?.message || 'Failed to save package')
}

const savePackage = async () => {
  error.value = ''
  if (!validatePackageForm()) {
    error.value = 'Fix the highlighted package fields and try again.'
    await showErrorToast(error.value)
    return
  }
  saving.value = true
  try {
    const isEditing = Boolean(editingPackageId.value)
    if (moduleType.value === 'vendor') {
      await saveVendorPackage()
      saveVendorDraftPayload()
    } else {
      await saveVenuePackage()
      await loadVenuePackages()
    }
    resetPackageForm()
    packageFormVisible.value = false
    if (isPackageEditorRoute.value) {
      await showSuccessToast(isEditing ? 'Package updated successfully' : 'Package added successfully')
      await closePackageEditorRoute()
      return
    }
    closePackageForm()
    await showSuccessToast(isEditing ? 'Package updated successfully' : 'Package added successfully')
  } catch (err) {
    error.value = err?.message || 'Failed to save package'
    await showErrorToast(error.value)
  } finally {
    saving.value = false
  }
}

const deletePackage = async (packageId) => {
  if (!packageId) return
  error.value = ''
  deletingPackageId.value = String(packageId)
  try {
    if (moduleType.value === 'vendor') {
      vendorForm.packages = vendorForm.packages.filter((item) => String(item.id) !== String(packageId))
      saveVendorDraftPayload()
      await showSuccessToast('Package deleted successfully')
      return
    }
    const formData = new FormData()
    formData.append('package_id', packageId)
    const { data } = await api.deletePackageVenue(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) throw new Error(data?.msg || data?.message || 'Failed to delete package')
    await loadVenuePackages()
    await showSuccessToast('Package deleted successfully')
  } catch (err) {
    error.value = err?.message || 'Failed to delete package'
    await showErrorToast(error.value)
  } finally {
    deletingPackageId.value = ''
  }
}

const loadVenuePolicies = async () => {
  if (moduleType.value !== 'venue' || !currentVenueId.value) return
  try {
    const formData = new FormData()
    formData.append('venue_id', currentVenueId.value)
    const { data } = await api.editPoliciesVenue(formData)
    const item = normalizeApiCollection(data, ['policies', 'policy', 'items', 'list'])[0] || data?.data || data || {}
    Object.assign(venuePoliciesForm, {
      inHouseCatering: inferCateringInHouseValue(item?.in_house_catering),
      outsideCatering: inferOutsideCateringValue(item?.outside_catering),
      decorPolicy: inferDecorPolicyValue(item?.decor_policy),
      musicPolicy: inferMusicPolicyValue(item?.music_policy),
      alcoholInhouse: inferBinaryToggleValue(item?.alcohol_inhouse),
      alcoholOutside: inferBinaryToggleValue(item?.alcohol_outside),
      minGuests: item?.min_guests || '',
      maxGuests: item?.max_guests || '',
      decorTime: item?.decor_time || '',
      cleanTime: item?.clean_time || '',
      noiseMusic: item?.noise_music || '',
      otherHouseRules: item?.other_house_rules || '',
      cancelTerms: item?.cancel_terms || '',
      depositPercent: item?.deposit_percent || ''
    })
  } catch {
    // Keep defaults when no policy is present yet.
  }
}

const saveVenuePolicies = async () => {
  if (!currentVenueId.value) throw new Error('Save venue identity first before adding policies.')
  const formData = new FormData()
  formData.append('venue_id', currentVenueId.value)
  formData.append('in_house_catering', venuePoliciesForm.inHouseCatering || '')
  formData.append('outside_catering', venuePoliciesForm.outsideCatering || '')
  formData.append('decor_policy', venuePoliciesForm.decorPolicy || '')
  formData.append('music_policy', venuePoliciesForm.musicPolicy || '')
  formData.append('alcohol_inhouse', policyAlcoholInhouseEnabled.value ? '1' : '0')
  formData.append('alcohol_outside', policyAlcoholOutsideEnabled.value ? '1' : '0')
  formData.append('min_guests', venuePoliciesForm.minGuests || '')
  formData.append('max_guests', venuePoliciesForm.maxGuests || '')
  formData.append('decor_time', venuePoliciesForm.decorTime || '')
  formData.append('clean_time', venuePoliciesForm.cleanTime || '')
  formData.append('noise_music', venuePoliciesForm.noiseMusic || '')
  formData.append('other_house_rules', venuePoliciesForm.otherHouseRules || '')
  formData.append('cancel_terms', venuePoliciesForm.cancelTerms || '')
  formData.append('deposit_percent', venuePoliciesForm.depositPercent || '')
  const { data } = await api.addPoliciesVenue(formData)
  const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
  if (isError) throw new Error(data?.msg || data?.message || 'Failed to save venue policies')
}

const toggleAlcoholPolicy = (target) => {
  if (target === 'inhouse') {
    venuePoliciesForm.alcoholInhouse = policyAlcoholInhouseEnabled.value ? '0' : '1'
    return
  }
  venuePoliciesForm.alcoholOutside = policyAlcoholOutsideEnabled.value ? '0' : '1'
}

const syncOutsideCateringFeeState = () => {
  if (!showOutsideCateringFeePanel.value) {
    venuePoliciesForm.outsideCateringFeeType = 'No fee'
    venuePoliciesForm.outsideCateringFeeValue = ''
    return
  }
  if (venuePoliciesForm.outsideCateringFeeType === 'No fee') {
    venuePoliciesForm.outsideCateringFeeValue = ''
  }
}

const savePolicies = async () => {
  error.value = ''
  saving.value = true
  try {
    await saveVenuePolicies()
    await loadVenuePolicies()
    await showSuccessToast('Venue policies saved successfully')
  } catch (err) {
    error.value = err?.message || 'Failed to save venue policies'
    await showErrorToast(error.value)
  } finally {
    saving.value = false
  }
}

const publishVenueOverview = async () => {
  if (!canPublishVenue.value) return
  error.value = ''
  publishingVenue.value = true
  try {
    const formData = new FormData()
    formData.append('venue_id', currentVenueId.value)
    const { data } = await api.publishVendorVenueDetails(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) throw new Error(data?.msg || data?.message || 'Failed to publish venue')
    await showSuccessToast('Venue published successfully')
  } catch (err) {
    error.value = err?.message || 'Failed to publish venue'
    await showErrorToast(error.value)
  } finally {
    publishingVenue.value = false
  }
}

const loadVenueSeoDetails = async () => {
  if (moduleType.value !== 'venue') return
  if (!currentVenueId.value) {
    Object.assign(venueSeoForm, {
      slug: listingPreviewSlug.value,
      metaDescription: ''
    })
    return
  }
  try {
    const formData = new FormData()
    formData.append('venue_id', currentVenueId.value)
    const { data } = await api.getVenueMetaDetails(formData)
    const item = normalizeApiCollection(data, ['meta', 'seo', 'items', 'list'])[0] || data?.data || data || {}
    Object.assign(venueSeoForm, {
      slug: item?.slug || listingPreviewSlug.value,
      metaDescription: item?.meta_description || item?.metaDescription || ''
    })
  } catch {
    Object.assign(venueSeoForm, {
      slug: listingPreviewSlug.value,
      metaDescription: ''
    })
  }
}

const saveVenueSeoDetails = async () => {
  if (!currentVenueId.value) throw new Error('Save venue identity first before updating SEO details.')
  const formData = new FormData()
  formData.append('venue_id', currentVenueId.value)
  formData.append('slug', venueSeoPreviewSlug.value)
  formData.append('meta_description', venueSeoForm.metaDescription || '')
  const { data } = await api.updateVenueMetaDetails(formData)
  const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
  if (isError) throw new Error(data?.msg || data?.message || 'Failed to save SEO details')
  venueSeoForm.slug = venueSeoPreviewSlug.value
}

const saveVenueSeoSection = async () => {
  error.value = ''
  saving.value = true
  try {
    await saveVenueSeoDetails()
    await loadVenueSeoDetails()
    await showSuccessToast('SEO details saved successfully')
  } catch (err) {
    error.value = err?.message || 'Failed to save SEO details'
    await showErrorToast(error.value)
  } finally {
    saving.value = false
  }
}

const resetVenueAvailabilityForm = () => {
  venueAvailabilityForm.leadTimeWeeks = ''
  venueAvailabilityForm.maxAdvanceBookingWeeks = ''
  venueAvailabilityForm.operatingHours.forEach((day) => {
    day.enabled = true
    day.from = '09:00'
    day.to = '23:00'
  })
}

const loadVenueAvailability = async () => {
  if (moduleType.value !== 'venue') return
  if (!currentVenueId.value) {
    resetVenueAvailabilityForm()
    return
  }
  try {
    const formData = new FormData()
    formData.append('venue_id', currentVenueId.value)
    const { data } = await api.getVenueAvailability(formData)
    const item = normalizeApiCollection(data, ['availability', 'items', 'list'])[0] || data?.data || data || {}
    venueAvailabilityForm.leadTimeWeeks = String(item?.lead_time || item?.leadTime || '').trim()
    venueAvailabilityForm.maxAdvanceBookingWeeks = String(item?.max_advance_booking || item?.maxAdvanceBooking || '').trim()
    venueAvailabilityForm.operatingHours.forEach((day) => {
      const fromValue = String(item?.[`${day.apiPrefix}_from_time`] || '').trim()
      const toValue = String(item?.[`${day.apiPrefix}_to_time`] || '').trim()
      day.enabled = Boolean(fromValue || toValue)
      day.from = fromValue || '09:00'
      day.to = toValue || '23:00'
    })
  } catch {
    resetVenueAvailabilityForm()
  }
}

const saveVenueAvailability = async () => {
  if (!currentVenueId.value) throw new Error('Save venue identity first before updating availability.')
  const formData = new FormData()
  formData.append('venue_id', currentVenueId.value)
  formData.append('lead_time', venueAvailabilityForm.leadTimeWeeks || '')
  formData.append('max_advance_booking', venueAvailabilityForm.maxAdvanceBookingWeeks || '')
  venueAvailabilityForm.operatingHours.forEach((day) => {
    formData.append(`${day.apiPrefix}_from_time`, day.enabled ? (day.from || '') : '')
    formData.append(`${day.apiPrefix}_to_time`, day.enabled ? (day.to || '') : '')
  })
  const { data } = await api.updateAvailabilityVenue(formData)
  const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
  if (isError) throw new Error(data?.msg || data?.message || 'Failed to save availability')
}

const saveVenueAvailabilitySection = async () => {
  error.value = ''
  saving.value = true
  try {
    await saveVenueAvailability()
    await loadVenueAvailability()
    await showSuccessToast('Availability saved successfully')
  } catch (err) {
    error.value = err?.message || 'Failed to save availability'
    await showErrorToast(error.value)
  } finally {
    saving.value = false
  }
}

const loadVenuePartners = async () => {
  if (moduleType.value !== 'venue' || !currentVenueId.value) {
    venuePartners.value = []
    return
  }
  try {
    const formData = new FormData()
    formData.append('venue_id', currentVenueId.value)
    const { data } = await api.listVenueVendorPartners(formData)
    const items = normalizeApiCollection(data, ['partners', 'vendor_partners', 'items', 'list'])
    venuePartners.value = items.map((item, index) => ({
      ...parseCombinedPhoneNumber(item?.phone || ''),
      id: item?.vendor_id || item?.venue_vendor_id || item?.id || `partner-${index}`,
      name: item?.name || '',
      category: resolvePartnerCategoryValue(item?.category_id || item?.vendor_category_id || item?.category || ''),
      categoryLabel: String(item?.category_name || item?.category_label || getPartnerCategoryLabel(item?.category_id || item?.vendor_category_id || item?.category || '')).trim(),
      displayPhone: item?.phone || ''
    }))
  } catch {
    venuePartners.value = []
  }
}

const saveVenuePartner = async () => {
  if (!currentVenueId.value) throw new Error('Save venue identity first before adding partners.')
  const formData = new FormData()
  if (editingPartnerId.value) {
    formData.append('vendor_id', editingPartnerId.value)
  } else {
    formData.append('venue_id', currentVenueId.value)
  }
  formData.append('name', partnerForm.name || '')
  formData.append('category', partnerForm.category || '')
  formData.append('phone', buildPartnerPhoneValue())
  const response = editingPartnerId.value ? await api.updateVenueVendorPartner(formData) : await api.addVenueVendorPartner(formData)
  const data = response?.data || {}
  const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
  if (isError) throw new Error(data?.msg || data?.message || 'Failed to save vendor partner')
}

const savePartner = async () => {
  error.value = ''
  if (!validatePartnerForm()) {
    error.value = 'Fix the highlighted vendor partner fields and try again.'
    await showErrorToast(error.value)
    return
  }
  saving.value = true
  try {
    const isEditing = Boolean(editingPartnerId.value)
    await saveVenuePartner()
    await loadVenuePartners()
    resetPartnerForm()
    partnerFormVisible.value = false
    if (isPartnerEditorRoute.value) {
      await showSuccessToast(isEditing ? 'Vendor partner updated successfully' : 'Vendor partner added successfully')
      await closePartnerEditorRoute()
      return
    }
    closePartnerForm()
    await showSuccessToast(isEditing ? 'Vendor partner updated successfully' : 'Vendor partner added successfully')
  } catch (err) {
    error.value = err?.message || 'Failed to save vendor partner'
    await showErrorToast(error.value)
  } finally {
    saving.value = false
  }
}

const deletePartner = async (partnerId) => {
  if (!partnerId) return
  error.value = ''
  deletingPartnerId.value = String(partnerId)
  try {
    const formData = new FormData()
    formData.append('venue_vendor_id', partnerId)
    const { data } = await api.deleteVenueVendorPartner(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) throw new Error(data?.msg || data?.message || 'Failed to delete vendor partner')
    await loadVenuePartners()
    await showSuccessToast('Vendor partner deleted successfully')
  } catch (err) {
    error.value = err?.message || 'Failed to delete vendor partner'
    await showErrorToast(error.value)
  } finally {
    deletingPartnerId.value = ''
  }
}

const openVenueGallery = (startIndex = 0) => {
  if (!venueGallery.value.length) return
  const instance = lightGallery(document.createElement('div'), {
    dynamic: true,
    dynamicEl: venueGallery.value.map((image) => ({
      src: image.src,
      thumb: image.src
    })),
    index: startIndex,
    plugins: [lgThumbnail, lgZoom],
    download: false,
    actualSize: false
  })
  instance.openGallery(startIndex)
}

const deleteVenueGalleryImage = async (imageId) => {
  if (!imageId) {
    await showErrorToast('Unable to delete this image right now.')
    return
  }
  error.value = ''
  saving.value = true
  try {
    const formData = new FormData()
    formData.append('id', imageId)
    const { data } = await api.deleteVenueGallery(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) throw new Error(data?.msg || data?.message || 'Failed to delete venue gallery image')
    await loadVenueGallery()
    await showSuccessToast('Image removed successfully')
  } catch (err) {
    error.value = err?.message || 'Failed to delete venue gallery image'
    await showErrorToast(error.value)
  } finally {
    saving.value = false
  }
}

const setVenueGalleryCover = async (image) => {
  const imageId = String(image?.id || '').trim()
  if (!imageId || !currentVenueId.value || image?.isCover) return

  error.value = ''
  updatingVenueCoverId.value = imageId

  try {
    const formData = new FormData()
    formData.append('image_id', imageId)
    formData.append('venue_id', currentVenueId.value)
    const { data } = await api.updateVenueCoverImage(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) throw new Error(data?.msg || data?.message || 'Failed to update venue cover image')
    await loadVenueGallery()
    await showSuccessToast('Venue cover image updated successfully')
  } catch (err) {
    error.value = err?.message || 'Failed to update venue cover image'
    await showErrorToast(error.value)
  } finally {
    updatingVenueCoverId.value = ''
  }
}

const loadVenueGalleryCategories = async () => {
  if (moduleType.value !== 'venue') return
  try {
    const { data } = await api.getVenueGalleryCategories(new FormData())
    const items = normalizeApiCollection(data, ['categories', 'gallery_categories', 'items', 'list'])
    const categories = items.map(normalizeGalleryCategory)
    venueGalleryCategories.value = categories.length
      ? [{ id: allVenueGalleryTagId, label: 'All' }, ...categories]
      : []
    if (!venueGalleryCategories.value.length && data?.data && !Array.isArray(data.data)) {
      venueGalleryCategories.value = [{ id: allVenueGalleryTagId, label: 'All' }, normalizeGalleryCategory(data.data, 0)]
    }
  } catch {
    venueGalleryCategories.value = []
  }
  if (!activeVenueGalleryTag.value && venueGalleryCategories.value.length) {
    activeVenueGalleryTag.value = venueGalleryCategories.value[0].id
  }
}

const loadVenueGallery = async () => {
  if (moduleType.value !== 'venue' || !currentVenueId.value || !activeVenueGalleryTag.value) {
    venueGallery.value = []
    return
  }
  try {
    if (activeVenueGalleryTag.value === allVenueGalleryTagId) {
      const categories = venueGalleryCategories.value.filter((item) => item.id !== allVenueGalleryTagId)
      const responses = await Promise.all(
        categories.map(async (category) => {
          const formData = new FormData()
          formData.append('venue_id', currentVenueId.value)
          formData.append('tag', category.id)
          const { data } = await api.getVenueGallery(formData)
          const payload = data?.data || data || {}
          const items = normalizeApiCollection(data, ['gallery', 'images', 'items', 'list'])
          return normalizeVenueGalleryEntries(items.length ? items : payload)
        })
      )
      venueGallery.value = responses
        .flat()
        .filter(Boolean)
        .filter((entry, index, collection) => collection.findIndex((item) => item.id === entry.id && item.src === entry.src) === index)
      return
    }

    const formData = new FormData()
    formData.append('venue_id', currentVenueId.value)
    formData.append('tag', activeVenueGalleryTag.value)
    const { data } = await api.getVenueGallery(formData)
    const payload = data?.data || data || {}
    const items = normalizeApiCollection(data, ['gallery', 'images', 'items', 'list'])
    venueGallery.value = normalizeVenueGalleryEntries(items.length ? items : payload)
  } catch {
    venueGallery.value = []
  }
}

const saveVenueGalleryImages = async () => {
  if (!currentVenueId.value) throw new Error('Save venue identity first before uploading gallery images.')
  if (!uploadableVenueGalleryTag.value) throw new Error('Select a specific gallery category first.')
  const files = venueGalleryFiles.value.map((entry) => entry?.file).filter(Boolean)
  const hasFiles = files.length > 0
  if (!hasFiles) throw new Error('Select at least one image to upload.')
  for (const file of files) {
    const formData = new FormData()
    formData.append('venue_id', currentVenueId.value)
    formData.append('tag', uploadableVenueGalleryTag.value)
    formData.append('image', file)
    const { data } = await api.addVenueGallery(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) throw new Error(data?.msg || data?.message || 'Failed to upload venue gallery')
  }
}

const saveGallery = async () => {
  error.value = ''
  venueGalleryUploading.value = true
  try {
    await saveVenueGalleryImages()
    await loadVenueGallery()
    venueGalleryFiles.value = []
    await showSuccessToast('Venue gallery updated successfully')
  } catch (err) {
    error.value = err?.message || 'Failed to update venue gallery'
    await showErrorToast(error.value)
  } finally {
    venueGalleryUploading.value = false
  }
}

const loadVenueSpaces = async () => {
  if (moduleType.value !== 'venue') return
  if (!currentVenueId.value) {
    venueSpaces.value = readStoredSpaces()
    return
  }
  try {
    const formData = new FormData()
    formData.append('venue_id', currentVenueId.value)
    const { data } = await api.listVenueSpaces(formData)
    const items = extractArrayPayload(data, ['spaces', 'venue_spaces', 'items', 'list'])
    venueSpaces.value = items.map((item, index) => ({
      ...(parseSeatConfigEntries(item?.seat_config || item?.seatConfig).configurations.length
        ? {
            seatingConfigurations: parseSeatConfigEntries(item?.seat_config || item?.seatConfig).configurations,
            rawSeatConfig: item?.seat_config || item?.seatConfig || ''
          }
        : {
            seatingConfigurations: splitToArray(item?.seat_config || item?.seatConfig),
            rawSeatConfig: item?.seat_config || item?.seatConfig || ''
          }),
      id: item?.id || item?.space_id || `space-${index}`,
      name: item?.name || item?.title || 'Untitled space',
      spaceType: resolveSpaceTypeValue(item?.space_type_id || item?.type_id || item?.type || item?.space_type || ''),
      spaceTypeLabel: String(item?.space_type_name || item?.type_name || getSpaceTypeLabel(item?.space_type_id || item?.type_id || item?.type || item?.space_type || '')).trim(),
      setting: item?.setting || '',
      areaSqft: item?.area_sqft || item?.areaSqft || '',
      seatingCapacities: splitToArray(item?.seat_capacity || item?.seatCapacity),
      minPrice: item?.min_price || item?.minPrice || '',
      maxPrice: item?.max_price || item?.maxPrice || '',
      amenities: splitToArray(item?.amenities),
      description: item?.description || ''
    }))
  } catch {
    venueSpaces.value = readStoredSpaces()
  }
}

const loadVenueModuleDetail = async () => {
  if (moduleType.value !== 'venue' || !currentVenueId.value) return
  try {
    const formData = new FormData()
    formData.append('venue_id', currentVenueId.value)
    const { data } = await api.listVenuesBusinessVendor(formData)
    const items = extractArrayPayload(data, ['venues', 'items', 'list', 'venue_list'])
    if (items.length) {
      populateVenueModuleFromApi(items[0])
    }
  } catch {
    // Fall back to local draft hydration already attempted above.
  }
}

const saveVenueModule = async () => {
  const vendorId = getAuthUserId()
  if (!vendorId) throw new Error('Vendor id is not available. Sign in again.')
  if (!businessId.value) throw new Error('Business id is not available.')
  if (!validateVenueIdentityForm()) throw new Error('Fix the highlighted venue fields and try again.')

  const formData = new FormData()
  const venueId = String(currentVenueId.value || '').trim()
  const isExistingVenue = Boolean(venueId && route.name === 'business-module-edit')
  formData.append('vendor_id', vendorId)
  formData.append('business_id', businessId.value)
  if (isExistingVenue) {
    formData.append('venue_id', venueId)
    formData.append('id', venueId)
  }
  formData.append('title', venueForm.title || '')
  formData.append('type', venueForm.type || '')
  formData.append('tag_line', venueForm.tagLine || '')
  formData.append('about', venueForm.about || '')
  formData.append('address', venueForm.address || '')
  formData.append('latitude', venueForm.latitude || '')
  formData.append('longitude', venueForm.longitude || '')
  formData.append('phone_code', venueForm.phoneCode || defaultCountryPhoneCode)
  formData.append('phone', venueForm.phone || '')
  formData.append('email', venueForm.email || '')
  formData.append('website', venueForm.website || '')
  formData.append('instagram', venueForm.instagram || '')
  formData.append('facebook', venueForm.facebook || '')
  formData.append('youtube', venueForm.youtube || '')
  formData.append('highlights', venueForm.highlights.join(','))

  const request = isExistingVenue ? api.updateVenue(formData) : api.addVendorBusinessVenue(formData)
  const { data } = await request
  const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
  if (isError) throw new Error(data?.msg || data?.message || 'Failed to save venue')

  const savedId =
    venueId ||
    data?.venue_id ||
    data?.id ||
    data?.data?.venue_id ||
    data?.data?.id ||
    data?.data?.insert_id ||
    moduleId.value ||
    String(Date.now())

  venueForm.id = String(savedId)

  return {
    ...buildPayload(),
    id: String(savedId)
  }
}

const saveVenueDetails = async () => {
  if (!currentVenueId.value) throw new Error('Save venue identity first before saving venue details.')
  if (!validateVenueDetailsForm()) throw new Error('Fix the highlighted venue detail fields and try again.')
  const formData = new FormData()
  formData.append('venue_id', currentVenueId.value)
  formData.append('venue_type', venueDetailsForm.venueType || '')
  formData.append('est_year', venueDetailsForm.yearEstablished || '')
  formData.append('events_hosted', venueDetailsForm.eventsHosted.join(','))
  formData.append('amenities', venueDetailsForm.amenities.join(','))
  formData.append('in_house_services', venueDetailsForm.inHouseServices.join(','))
  const { data } = await api.updateVendorVenueDetails(formData)
  const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
  if (isError) throw new Error(data?.msg || data?.message || 'Failed to save venue details')
}

const saveVenueDetailsSection = async (exitAfterSave) => {
  error.value = ''
  saving.value = true
  try {
    await saveVenueDetails()
    const items = loadModules()
    const payload = buildPayload()
    const index = items.findIndex((entry) => String(entry.id) === String(payload.id))
    if (index === -1) {
      items.unshift(payload)
    } else {
      items[index] = payload
    }
    saveModules(items)
    await showSuccessToast('Venue details saved successfully')
    if (exitAfterSave) {
      goBack()
    }
  } catch (err) {
    error.value = err?.message || 'Failed to save venue details'
    await showErrorToast(error.value)
  } finally {
    saving.value = false
  }
}

const saveVendorPoliciesSection = async (exitAfterSave) => {
  error.value = ''
  saving.value = true
  try {
    saveVendorDraftPayload()
    await showSuccessToast('Vendor policies saved successfully')
    if (exitAfterSave) {
      goBack()
    }
  } catch (err) {
    error.value = err?.message || 'Failed to save vendor policies'
    await showErrorToast(error.value)
  } finally {
    saving.value = false
  }
}

const saveVendorPricingSection = async () => {
  error.value = ''
  saving.value = true
  try {
    saveVendorDraftPayload()
    await showSuccessToast('Vendor pricing saved successfully')
  } catch (err) {
    error.value = err?.message || 'Failed to save vendor pricing'
    await showErrorToast(error.value)
  } finally {
    saving.value = false
  }
}

const saveVendorDraftPayload = () => {
  const items = loadModules()
  const payload = buildPayload()
  const index = items.findIndex((entry) => String(entry.id) === String(payload.id))
  if (index === -1) {
    items.unshift(payload)
  } else {
    items[index] = payload
  }
  saveModules(items)
}

const saveModule = async (exitAfterSave) => {
  if (moduleType.value === 'venue' && activeSection.value === 'details') {
    await saveVenueDetailsSection(exitAfterSave)
    return
  }
  error.value = ''
  saving.value = true
  try {
    if (moduleType.value === 'venue' && !validateVenueIdentityForm()) {
      throw new Error('Fix the highlighted venue fields and try again.')
    }
    const items = loadModules()
    const payload = moduleType.value === 'venue' ? await saveVenueModule() : buildPayload()
    const index = items.findIndex((entry) => String(entry.id) === String(payload.id))
    if (index === -1) {
      items.unshift(payload)
    } else {
      items[index] = payload
    }
    saveModules(items)
    await showSuccessToast(moduleType.value === 'venue' ? 'Venue saved successfully' : 'Module saved successfully')
    if (exitAfterSave) {
      goBack()
      return
    }
    await router.replace({
      name: 'business-module-edit',
      params: {
        businessId: businessId.value,
        moduleType: moduleType.value,
        moduleId: payload.id
      },
      query: { businessName: businessName.value }
    })
    if (moduleType.value === 'venue') {
      await Promise.all([
        loadVenueModuleDetail(),
        loadVenueSpaces(),
        loadVenuePackages(),
        loadVenuePolicies(),
        loadVenueAvailability(),
        loadVenuePartners(),
        loadVenueGallery(),
        loadVenueSeoDetails()
      ])
    }
  } catch (err) {
    error.value = err?.message || 'Failed to save module'
    await showErrorToast(error.value)
  } finally {
    saving.value = false
  }
}

watch(
  [sections, requestedSection],
  ([availableSections, section]) => {
    if (section && availableSections.some((item) => item.id === section)) {
      activeSection.value = section
      return
    }
    if (!availableSections.some((item) => item.id === activeSection.value)) {
      activeSection.value = availableSections[0]?.id || 'identity'
    }
  },
  { immediate: true }
)

watch(
  [isPartnerEditorRoute, requestedPartnerId, currentVenueId, venuePartners],
  async ([enabled, partnerId, venueId]) => {
    if (!enabled) {
      resetPartnerForm()
      partnerFormVisible.value = false
      return
    }
    if (!venueId) {
      resetPartnerForm()
      return
    }
    if (!isEditingPartnerRoute.value) {
      resetPartnerForm()
      partnerFormVisible.value = true
      return
    }
    if (hydratePartnerForm(partnerId)) return
    await loadVenuePartners()
    if (!hydratePartnerForm(partnerId)) {
      await showErrorToast('Vendor partner not found for editing.')
      closePartnerEditorRoute()
    }
  },
  { immediate: true }
)

watch(
  [isPackageEditorRoute, requestedPackageId, currentVenueId, moduleId, () => vendorForm.packages],
  async ([enabled, packageId, venueId, vendorId]) => {
    if (!enabled) {
      resetPackageForm()
      packageFormVisible.value = false
      return
    }
    const targetId = moduleType.value === 'venue' ? venueId : vendorId
    if (!targetId) {
      resetPackageForm()
      return
    }
    if (!isEditingPackageRoute.value) {
      resetPackageForm()
      packageFormVisible.value = true
      return
    }
    const hydrated = await hydratePackageForm(packageId)
    if (!hydrated) {
      closePackageEditorRoute()
    }
  },
  { immediate: true }
)

watch(
  [isSpaceEditorRoute, requestedSpaceId, currentVenueId, venueSpaces],
  async ([enabled, spaceId, venueId]) => {
    if (!enabled) {
      resetSpaceForm()
      spaceFormVisible.value = false
      return
    }
    if (!venueId) {
      resetSpaceForm()
      return
    }
    if (!isEditingSpaceRoute.value) {
      resetSpaceForm()
      spaceFormVisible.value = true
      return
    }
    const hydrated = hydrateSpaceForm(spaceId)
    if (hydrated) return
    await loadVenueSpaces()
    if (!hydrateSpaceForm(spaceId)) {
      await showErrorToast('Space not found for editing.')
      closeSpaceEditorRoute()
    }
  },
  { immediate: true }
)

onMounted(() => {
  fetchVenueHighlights()
  fetchVenueDetailOptions()
  loadVenueModuleDetail()
  loadVenueSpaces()
  loadVenuePackages()
  loadVenuePolicies()
  loadVenueAvailability()
  loadVenuePartners()
  loadVenueSeoDetails()
  loadVenueGalleryCategories().then(() => {
    loadVenueGallery()
  })
  bootstrapVenueLocation()
})

onBeforeUnmount(() => {
  clearVenueGalleryAutoUploadTimer()
  if (locationMap.value) {
    locationMap.value.remove()
    locationMap.value = null
  }
  locationMarker.value = null
})
</script>

<style scoped>
.venue-gallery-pond :deep(.filepond--root) {
  margin-bottom: 0;
}

.venue-gallery-pond :deep(.filepond--list-scroller) {
  margin: 0;
}

.venue-gallery-pond :deep(.filepond--list) {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
  gap: 12px;
  padding-top: 14px;
}

.venue-gallery-pond :deep(.filepond--item) {
  position: relative;
  inset: auto;
  width: 100%;
  margin: 0;
  transform: none !important;
}

.venue-gallery-pond :deep(.filepond--item-panel),
.venue-gallery-pond :deep(.filepond--image-preview),
.venue-gallery-pond :deep(.filepond--image-preview-wrapper),
.venue-gallery-pond :deep(.filepond--file-wrapper) {
  height: 132px !important;
}

.venue-gallery-pond :deep(.filepond--panel-root) {
  background: rgba(255, 255, 255, 0.82);
}

.venue-gallery-pond :deep(.filepond--image-preview img) {
  object-fit: cover;
}

@media (max-width: 1024px) {
  .venue-gallery-pond :deep(.filepond--list) {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
}

@media (max-width: 640px) {
  .venue-gallery-pond :deep(.filepond--list) {
    grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
    gap: 10px;
  }

  .venue-gallery-pond :deep(.filepond--item-panel),
  .venue-gallery-pond :deep(.filepond--image-preview),
  .venue-gallery-pond :deep(.filepond--image-preview-wrapper),
  .venue-gallery-pond :deep(.filepond--file-wrapper) {
    height: 104px !important;
  }
}
</style>
